export default {
  nameResolution: {
    requiredFailed:
      'Required surface {{name}} did not land. Review the extraction results before meshing.',
    requiredFailedCoolant:
      'The name {{name}} did not land on the carved coolant. Check it in Boundary Assignment, then carve again.',
    highlight: 'Highlight {{name}}',
    loading: 'Loading named surface results…',
    loadFailed: 'Named surface results could not be loaded.',
    retry: 'Retry',
    title: 'Named surfaces',
    landed: 'Landed',
    failed: 'Failed',
    coverage: '{{share}} of the prepared surface',
    coverageUnavailable: 'Surface share unavailable',
    mergedInto: 'Combined with {{name}}',
    reason: {
      not_boundary_opening:
        'This mouth is not on the coolant channel boundary. Choose a different detected mouth.',
      no_cad_record: 'No CAD face record was available for this name.',
      normal_disagreement:
        'The carved surface does not align with this named wall. Changing passage size will not fix this transfer.',
      no_match_in_band:
        'The carve could not resolve this named wall. Changing passage size will not fix this transfer.',

      spatial_match_failed: 'No matching prepared surface was found.',
      ambiguous_match: 'More than one surface matched this face.',
      no_descriptors: 'The original CAD face information is unavailable.',
      resolved: 'No resulting surface was recorded.',
      merged: 'No resulting surface was recorded for this combined name.',
      // Reserved OpenFOAM/FEA patch names are renamed at mesh time (ENG-3076).
      renamed:
        'This name is reserved; the surface landed under a generated patch name.',
    },
  },
  // MeshStatus labels for the mesh dot / retry log / file tree (ENG-1744).
  // Copy is byte-identical to the strings the hardcoded MESH_STATUS_LABEL map
  // shipped, so `meshRetryLog.test.ts` still asserts against real bundle
  // output. `recombining` deliberately reads the same as `meshing`: the
  // recombination pass is an implementation detail of mesh generation and
  // saying "Recombining" would name a step users have no control over.
  meshStatus: {
    completed: 'Completed',
    failed: 'Failed',
    meshing: 'Meshing...',
    recombining: 'Meshing...',
    pending: 'Pending',
    cancelled: 'Cancelled',
  },
  failureMessage: {
    // Localized reasons for common BE-emitted pipeline failure messages.
    // Matched by regex in utils/failureMessage.ts:translateFailureMessage
    // against the CLEANED English message. First-match wins.
    stlNotWatertight:
      "STL isn't watertight — open edges detected. Cap the openings in your CAD tool and re-export.",
    degenerateTriangles:
      'STL has degenerate (zero-area) triangles. Re-export with a finer chord/angular tolerance.',
    stlUnreadable:
      "Couldn't read the STL — the header may be malformed. Re-export from your CAD tool.",
    stlEmpty:
      'STL contains no triangles. The file may have been written empty — re-export and try again.',
    stlSingleSolid:
      'STL has no named solids to tag individual patches — meshing still works but boundary conditions will apply to the whole surface. Re-export as ASCII STL with named solids to tag per-patch.',
    selfIntersection:
      'Self-intersecting geometry — surfaces cross themselves. Fix the intersections in CAD (e.g. union overlapping bodies) and re-upload.',
    nonManifold:
      'Non-manifold edges — three or more faces meet at an edge, or a surface has a T-junction. Clean up in CAD (typically fuse or trim overlapping bodies).',
    pinchPoint:
      'Pinch point — two surfaces touch at a single point without sharing a face. Automatic healing collapses the solid; union the bodies in CAD so they share a face.',
    overlappingFaces:
      'Overlapping or coincident faces — two surfaces occupy the same space. Fuse redundant solids in CAD or remove the duplicate face.',
    couldNotHeal:
      "Automatic repair couldn't heal this geometry. The defects need a CAD-level fix — try simplify (fuse solids), or repair in your CAD tool and re-upload.",
    timedOut:
      'Diagnosis timed out — the geometry is too complex to analyze in the allowed budget. Try simplifying the model or removing small features.',
    outOfMemory:
      'Geometry ran the analyzer out of memory. Try splitting the assembly or removing small features / duplicate bodies.',
    unsupportedFormat:
      "File format isn't supported. Try re-exporting as STEP (recommended) or STL.",
    couldNotRead:
      'The file could not be read. Try re-exporting from your CAD tool with default settings.',
    zeroVolume:
      'Geometry has no solid volume — it may be a surface body or a sketch. Meshing needs a closed solid.',
    noGeometry:
      'No geometry found in the file. It may be empty, contain only points/curves, or use an unsupported format.',
    // Mesh pipeline (post-diagnosis) failure messages. Matched against
    // meshResults.error / failedStageReport.message in MeshResultsPanel.
    meshTimedOut:
      'Meshing timed out before it could finish this stage. Raise Strategy Timeout, coarsen Min Size, or lower the max refinement level.',
    meshOutOfMemory:
      'Meshing ran out of memory. Try coarsening Min Size, lowering the max refinement level, or splitting the assembly.',
    meshPolyBlCrash:
      'The polyhedral mesher crashed (SIGSEGV) — most often the boundary-layer refiner at fine cell sizes or on dirty surfaces. Retry without boundary layers, or with a coarser Max/Min Size.',
    meshPipelineFailed:
      'The mesh pipeline stopped before it could produce a result. See the logs and the stage report below for the failing step.',
    meshingFailedGeneric:
      'Meshing failed. See the logs and stage report below for the failing step.',
    meshAddLayers:
      'Boundary-layer extrusion (snappyHexMesh addLayers) failed on this run. Try lowering Layer Count, reducing First Layer Height, or ticking Prioritize BL Coverage.',
    meshSnapFailed:
      'The snap stage (snappyHexMesh snap) failed to converge on this geometry. Try widening the feature angle, enabling Snap Fidelity, or repairing the CAD.',
    meshFaceTagUnresolved:
      "A required inlet or outlet tag didn't match any face on the surface being meshed, so the run stopped before meshing started. This is a labelling problem, not a CAD problem. Open Boundary Assignment, check the Mesh Source (Fluid domain vs Body) matches the faces you tagged, and re-tag the inlet and outlet.",
    meshBlockmeshFailed:
      "blockMesh couldn't build the background mesh for this run. Check the enclosure factors and the BlockMesh Cell Size override.",
    meshWireball:
      'The snap produced degenerate / non-convex cells (wireball). Try enabling Snap Fidelity or upload the geometry via Wrap instead of Repair.',
    meshCellBudget:
      'The mesh would exceed the cell budget for this run. Raise Max Local Cells / Max Global Cells, or coarsen the mesh (larger Min Size, lower refinement).',
    // Reusable "after {{seconds}} s" fragment threaded via `{{after}}` on
    // substage-timeout keys. Empty when the BE didn't emit a seconds tail,
    // so the sentence stays clean instead of showing "after  s".
    afterSeconds: ' after {{seconds}} s',
    assemblySplitIgnored:
      'Assembly-split meshing ignored these overrides: {{overrides}}.',
    meshSnappyTimedOut:
      'snappyHexMesh (`openfoam_snappy`) timed out{{after}} — the process spent the entire strategy budget without producing a final mesh. Check the stage timeline above to see which internal step (blockMesh / snap / addLayers) was still running when the budget hit. Fix: coarsen Min Size, lower the max refinement level, or raise Strategy Timeout in Advanced.',
    meshBlockmeshTimedOut:
      'blockMesh (`openfoam_blockmesh`) timed out{{after}} while building the background grid. Coarsen the domain grid — either enlarge BlockMesh Cell Size or shrink the enclosure.',
    meshAddLayersTimedOut:
      'The addLayers stage (`openfoam.add_layers`) timed out{{after}} while extruding boundary-layer cells. Try fewer boundary-layer layers, a lower first layer height, or tick Prioritize BL Coverage.',
    meshSnapTimedOut:
      'The snap stage (`openfoam.snap`) timed out{{after}} while snapping the mesh to the CAD feature edges. Widen the feature angle, enable Snap Fidelity, or repair the CAD.',
    meshGmshTimedOut:
      'Gmsh timed out{{after}}. Coarsen Min Size or lower the refinement level.',
    // Post-mesh quality warnings — yellow chip row rendered on successful
    // runs when checkMesh flagged issues. Values interpolated from the raw
    // BE English via translateMeshWarning.
    warnHighSkewness:
      'Max skewness = {{value}}. {{count}} highly skewed faces detected — this may degrade solver accuracy near those cells. Consider raising the max refinement level or repairing the CAD in the flagged regions.',
    warnHighNonOrtho:
      'Max non-orthogonality = {{value}}°. {{count}} highly non-orthogonal faces detected — the solver may need more non-orthogonal correctors. Bump Non-Orth Correctors to 3–4, or lower Max Non-Ortho and re-mesh.',
    warnHighAspect:
      'Max aspect ratio = {{value}}. {{count}} highly stretched cells detected — this often reflects a boundary-layer / bulk mesh mismatch. Try a lower growth rate or a coarser Min Size.',
    warnNegativeVolume:
      "{{count}} negative-volume cells detected — the mesh isn't valid to solve on. Coarsen Min Size, lower the max refinement level, or repair the CAD near the flagged cells.",
    warnCheckMeshFailed:
      '{{count}} cells failed checkMesh. Solver may still run, but the results near those cells will be unreliable. Consider tightening the quality target or re-meshing.',
    warnBlCoverage:
      'Boundary-layer coverage {{actual}}% (target {{target}}%). Some walls got no or partial layers — enable Prioritize BL Coverage, or reduce Layer Count / First Layer Height.',
    warnZeroNegativeFaceArea:
      "Zero or negative face area detected (min = {{minArea}}). checkMesh flagged degenerate faces — the mesh isn't safe to solve on. Coarsen Min Size, lower the max refinement level, or repair the CAD near the flagged region.",
    warnFacePyramids:
      "{{count}} face(s) are incorrectly oriented (checkMesh facePyramids). Non-manifold or flipped-normal geometry — repair the CAD around those faces, or re-mesh with a coarser Min Size so snappy doesn't produce a degenerate face there.",
  },
  fastenerInterfaces: {
    title: 'Detected bolt interfaces ({{count}})',
    criteriaTooltip:
      'Listed when two cylinders sit in different bodies, run parallel, overlap along their axis, and the shank sits inside the bore.\n\nThe bore must also be no more than 4x the shank radius — without that bound a housing (an axle tube, a hub bore) claims every bolt that passes through it.\n\nClearance and offset are reported, never graded: a rivet fills its hole, a shoulder bolt is close, a slotted bracket is deliberately loose.',
    hint: 'Naming a pair labels its two faces. Pair them as contacts in SimOps, where the fit can still be adjusted.',
    loadFailed: 'Could not read fastener interfaces for this geometry.',
    notMeasured:
      'Fastener fit was not measured on this geometry — run diagnosis again to measure it.',
    legacyDiagnosis_one:
      '{{count}} pair has no face identity and cannot be applied. Run diagnosis again to record it.',
    legacyDiagnosis_other:
      '{{count}} pairs have no face identity and cannot be applied. Run diagnosis again to record them.',
    bodyPair: 'Bolt Vol {{shank}} in Vol {{hole}}',
    fit: 'Clearance {{clearance}} mm · offset {{eccentricity}} mm',
    interference: 'Shank is wider than its bore — check the model.',
    selectPair: 'Select bolt Vol {{shank}} in Vol {{hole}} for naming',
    applySelected_one: 'Name {{count}} selected pair',
    applySelected_other: 'Name {{count}} selected pairs',
    apply: 'Name faces',
    applied: 'Named',
    unbindable:
      'These faces are not in the current view; switch back to bind them.',
    verdict: {
      no_head_seating: 'No head seating',
      no_modelled_head: 'No modelled head',
    },
    verdictHint: {
      no_head_seating:
        'The head does not overhang the bore, so a preload would have nothing to bear against.',
      no_modelled_head:
        'No axis-facing flat face was found, so head seating could not be assessed.',
    },
  },
  bodyPartName: {
    tooltip: 'Part name from the CAD file',
  },
  structuralBodies: {
    title: 'Body Assignment',
    description:
      'Name each body and say what it is. Saved on the geometry, so every later step uses the same names. Labels are for reading only — the solver still calls each body Volume_<index>.',
    runDiagnosisFirst:
      'No bodies yet. Run diagnosis on this geometry to list its volumes.',
    volumeLabel: 'Vol {{index}}',
    labelFor: 'Name for volume {{index}}',
    hideBody: 'Hide volume {{index}}',
    showBody: 'Show volume {{index}}',
    roleFor: '{{role}} for volume {{index}}',
    roleMember: 'Member',
    roleFastener: 'Fastener',
    roleExcluded: 'Excluded',
    roleMemberTooltip:
      'A load-carrying part of the assembly. Meshed and solved.',
    roleFastenerTooltip:
      'A bolt, rivet or pin. Meshed and solved, and reported separately from the members it joins.',
    roleExcludedTooltip:
      'Left out of the mesh entirely — no elements, no results, no contact.',
    detectedBadge: 'Proposed',
    detectedTooltip:
      'Suggested by fastener detection and not confirmed yet. Pick a role to confirm or correct it.',
    save: 'Save assignment',
    saving: 'Saving…',
    savedToast: 'Body assignment saved.',
    conflictToast:
      'Someone else changed the body assignment. Your edits are kept — review and save again.',
    saveFailedToast: 'Could not save the body assignment. Try again.',
  },
  bodyClassification: {
    title: 'Body Classification',
    cavity: 'Cavity',
    body: 'Body',
    cavityTooltip:
      'Show the fluid passage (cavity STL). Hovering a fluid volume lights up its cavity faces.',
    bodyTooltip:
      'Show the wall geometry (body STL). Hovering a solid volume lights up its wall faces.',
    description:
      'Assign each CAD volume a role for multi-region CHT. Exactly one fluid and at least one solid region are required.',
    // ENG-3115: why the Cavity / Body switch is hidden, and what switching
    // Mesh from back clears (`setInternalFlowMeshSource`).
    meshFromBodyNote:
      'The Cavity view is off because “Mesh from” is set to Body in Boundary Assignment. Setting it back to Fluid domain clears the surface labels and boundary-layer wall picks on this part; then pick Cavity here.',
    runDiagnosisFirst: 'Run diagnosis first to detect CAD volumes.',
    volumesDetected_one:
      '{{count}} volume detected. Re-run diagnosis to populate volume details.',
    volumesDetected_other:
      '{{count}} volumes detected. Re-run diagnosis to populate volume details.',
    volumeLabel: 'Vol {{index}}',
    regionLabelPlaceholder: 'Region label',
    invalidLabels:
      'Labels must use only letters, digits, and underscores (no spaces or special characters).',
    duplicateLabels_one: 'Duplicate label:',
    duplicateLabels_other: 'Duplicate labels:',
    assignOneFluid: 'Assign exactly one volume as Fluid. ',
    onlyOneFluid: 'Only one Fluid region is allowed. ',
    atLeastOneSolid: 'At least one Solid region is required.',
    validSummary:
      '{{fluidCount}} fluid + {{solidCount}} solid region(s) configured.',
    cavityBadge: 'Extracted',
    cavityBadgeTooltip:
      'The hollow space inside your part. We found it from the geometry — it is not one of the bodies in your CAD file, and it is usually the fluid.',
    roleFluid: 'Fluid',
    roleSolid: 'Solid',
    roleExclude: 'Exclude',
    // ENG-3052: the wrap path, which names the parts of a wrap upload. Every
    // part is a Solid there, so the role buttons give way to one fixed tag.
    descriptionWrap:
      'Name each part before meshing. Each name becomes that part’s region in the mesh and in SimOps.',
    coolantRow: 'Coolant',
    coolantRowNote:
      'Carved from the channel in Boundary Assignment, so it is not one of your CAD parts.',
    solidOnlyTooltip:
      'On a wrap mesh every CAD part is a solid. The coolant is carved from the channel, so no part is the fluid, and no part can be left out because that would change the carve.',
    noHighlight: 'The 3D view can’t highlight these parts yet.',
    // ENG-3119: the wrap Cavity / Body switch. Cavity shows the carve, and the
    // 3D view lights no part there, so Repair's `cavityTooltip` would be false.
    cavityTooltipWrap:
      'Show the carved coolant inside a see-through wall. For looking only.',
    bodyTooltipWrap: 'Show the parts. Hover a card to light its part up.',
    cavityLookOnlyNote:
      'Cavity view is for looking only: parts don’t light up and a click picks nothing. Switch to Body to find a part.',
    waitingForPartCheck:
      'Still checking this upload for separate parts. They appear here when the check finishes.',
    partsCannotBeNamed: 'This upload’s parts can’t be named.',
    // One line per refused name, mirroring what the backend refuses.
    refusals: {
      blank: 'A name can’t be blank.',
      tooLong: '“{{label}}” is longer than {{max}} characters.',
      folderName: '“{{label}}” is a folder name OpenFOAM uses for itself.',
      interfaceInfix:
        '“{{label}}” contains _to_, which OpenFOAM uses to name the walls between regions.',
      interfaceSuffix:
        '“{{label}}” ends in _to, so the walls next to it would be read back as the wrong regions.',
      sameFolder:
        '“{{other}}” and “{{label}}” differ only in capital letters, and macOS and Windows treat them as one folder.',
      sameRegion: '“{{other}}” and “{{label}}” become the same region name.',
      coolantName: '“{{label}}” is the coolant’s name.',
      solidName:
        '“{{label}}” is the name the mesh gives the metal when it can’t split the parts.',
      airName:
        '“{{label}}” is kept for pockets of trapped air (air_0, air_1 and so on).',
      otherPartDefault: '“{{label}}” is another part’s default name.',
    },
  },
  boundaryAssignment: {
    suggestedLabels: {
      fixed: 'Fixed support',
      load: 'Load',
      pressure: 'Pressure',
      symmetry: 'Symmetry',
      contact: 'Contact',
      surface: 'Surface',
      wall: 'Wall',
      inlet: 'Inlet',
      outlet: 'Outlet',
      farfield: 'Far field',
    },
    title: 'Boundary Assignment',
    preview: 'Preview',
    faceLabelsConflictToast:
      "Couldn't save your surface labels — they changed in another session. Refresh to load the latest.",
    descriptionFea:
      'Label CAD surfaces before meshing for boundary conditions setting per surface in SimOps.',
    descriptionFlow:
      'Assign boundary roles to each surface. Detected openings are pre-labelled; remaining faces default to wall.',
    // ENG-3072: wrap CHT with inside cooling, where this panel carves the
    // coolant and lists no surfaces.
    descriptionCoolant:
      'Find the openings the coolant flows through, name them if you like, then carve the coolant out of the part. Openings you leave blank are named for you, and SimOps asks which way the coolant flows through any whose name doesn’t say.',
    // ENG-1869: says what the user is looking at, and names no CULPRIT and no
    // remedy. It does still name the cause — "no fluid cavity has been
    // extracted" — because that is the true and useful half of the copy it
    // replaced. What went was the blame ("common with multi-volume parts") and
    // the prescription ("until the geometry is re-uploaded as a single
    // volume"), both written when extraction was a silent step inside
    // diagnosis. ENG-1677 made it a step of its own, so re-upload stopped being
    // the way out; where the user CAN act, `cavityNotExtracted` names the
    // Extract button instead, and this string is suppressed. Deliberately
    // prescribes nothing itself: it also renders on wrap rows, where Extract IS
    // on screen but running it does not clear the condition (the face list is
    // body-space either way), so naming it there would send the user in a
    // circle. No `{{count}}`: the openings count it interpolated came from
    // diagnosis, and no sentence needs it now.
    bodySurfaceFallbackTitle: 'Tagging will bind to the solid body',
    bodySurfaceFallbackBody:
      "No fluid cavity has been extracted for this geometry, so the surfaces below are the solid body's rather than the cavity's. Inlet and outlet faces need tagging by hand, and the meshed cavity cannot be previewed.",
    hideEnclosure: 'Hide enclosure',
    showEnclosure: 'Show enclosure',
    enclosureFaces: 'Enclosure Faces',
    resetAllToDefaults: 'Reset all to defaults',
    reset: 'Reset',
    faceReadoutWithDiameter:
      'Per-face read-out. plane, cylinder … = the surface the face lies on in the CAD, a cylinder with its diameter (not shown for STL parts); Ø = equivalent circular diameter (2·√(area/π)) — a quick opening-size proxy, not a literal size for non-circular faces; cm² = face surface area; n = outward unit normal.',
    faceReadout:
      'Per-face read-out. plane, cylinder … = the surface the face lies on in the CAD, a cylinder with its diameter (not shown for STL parts); cm² = face surface area; n = outward unit normal.',
    surfaceType: {
      plane: 'plane',
      cylinder: 'cylinder',
      cone: 'cone',
      sphere: 'sphere',
      torus: 'torus',
      bspline: 'B-spline',
      other: 'other surface',
    },
    wallGroup: 'wall ({{count}})',
    labelSummary: 'Label Summary',
    facesCount: '{{count}} faces',
    boundaryRolesTooltip:
      'What the boundary roles mean for CFD:\n• Wall — solid no-slip surface; the fluid sticks to it (zero velocity at the wall).\n• Inlet — flow enters here; you set the velocity / flow rate.\n• Outlet — flow leaves here; you set the pressure (usually 0 gauge).\n• Symmetry — a mirror plane nothing crosses; lets you mesh half a symmetric domain.\n• Slip — frictionless wall: no flow through it, but no drag along it.\n• Farfield / Freestream — open boundary far from the body where the flow is undisturbed (external flow).',
    sharedBoundaryNotice:
      'Faces with the same name will share one boundary condition.',
    surfaceZones: 'Surface Zones',
    selectByVolume: 'Select faces by volume',
    volumeFaces: 'Volume {{index}} · {{count}} faces',
    volumeSelectionHint:
      'Selects every surface belonging to the volume in the 3D viewer. Individual surface rows add to or remove from this selection.',
    selectedFaceName: 'Name {{count}} selected faces',
    faceNamePlaceholder: 'e.g. symmetry or fixed',
    renameSelected: 'Rename',
    surfaceCountTooltip:
      'The number of distinct surfaces detected on this part. Read live from the server, so it always matches the surfaces that will be meshed.',
    loadingSurfaces: 'Loading surfaces…',
    surfacesNotComputed: 'Surfaces not yet computed. Complete diagnosis first.',
    // ENG-1816: not a defect report — the extraction is a step of its own
    // since ENG-1677, and this names it rather than describing damage.
    cavityNotExtracted:
      'Extract the fluid cavity first — Boundary Assignment tags faces on the cavity, not on the solid body. Run Extract in Mesh Settings, then come back.',
    noSurfaces: 'No surfaces available. Complete diagnosis first.',
    // ENG-979: Wrap never runs diagnosis, so `noSurfaces` above names a step
    // that never happens. Not futile though — ENG-1010 carries names onto the
    // wrap by `face_index`, hence "check the mesh results".
    // ENG-2361: the fallback for a row that got no face list, not a statement
    // about Wrap as a prep -- a wrap row does get one when extraction succeeds.
    noSurfacesWrap:
      "No CAD faces are available for this part. Face lists are worked out in the background after upload, and some parts are too large or too complex to finish. Names are still applied to the wrapped surface when you mesh — mesh the part, then check the mesh results to see which names landed and which didn't.",
    // ENG-2361. "Under a minute" rather than a figure: measured successes ran
    // 1–21 s, but a part can also sit here until the worker budget expires and
    // then fall back to `noSurfacesWrap` above, so a tighter promise would be
    // broken by exactly the parts that wait longest.
    wrapDescriptorsPending:
      'Working out which CAD faces this part has. This runs in the background after upload and usually takes under a minute — the list will appear here when it is ready.',
    surfaceNumberingMismatch:
      "The surface list came back with face numbering that doesn't match this part, so labelling is disabled — labels could land on the wrong faces. Re-detect to rebuild the list.",
    reDetecting: 'Re-detecting…',
    loading: 'Loading…',
    reDetect: 'Re-detect',
    // ENG-3091: a rejected Re-detect with no message of its own. A
    // seat/subscription refusal shows `compCommon.entitlement` instead.
    reDetectFailed: "Couldn't re-detect surfaces. Try again.",
    noPatchesStl:
      "This STL has no named surfaces to tag — binary or single-solid files don't include them. Meshing still works; boundary conditions apply to the whole surface. Re-export as ASCII STL with named solids to tag individual patches.",
    seedPointTitle: 'Seed point (optional)',
    seedPointHide: 'Hide seed marker',
    seedPointShow: 'Show seed marker',
    seedPointHelper:
      'A point inside the fluid cavity, in model coordinates (mm). Auto-detected for simple shapes — set it when the cavity is non-convex and auto-detection can land outside the fluid. The amber dot in the viewer shows where it lands.',
    seedPointAuto: 'auto',
    seedPointUseCenter: 'Use part center',
  },
  // The four guarded cavity steps in Boundary Assignment on the wrap path (ENG-2465).
  // Copy is the design handoff's copy sheet (Boundary Assignment Wireframes, 3b).
  cavitySteps: {
    // shell — step titles and shared words (ENG-2465)
    detect: 'Find the openings',
    nameOpenings: 'Name each opening',
    nameZones: 'Name wall areas',
    carve: 'Carve the fluid volume',
    // ENG-3072: wrap CHT's three steps (no wall-name step).
    carveCoolant: 'Carve the coolant',
    optional: 'optional',
    // openings — step 2 copy (ENG-2466 appends here)
    // zones — step 3 copy (ENG-2430 appends here)
    zoneNameFor: 'Name for face {{face}}',
    wasDetectedOpening: 'Was a detected opening',
    // gates — step status, locked reasons and summaries (ENG-2465 PR 2)
    scanning: 'Scanning the part…',
    detectDone_one: '{{count}} opening found',
    detectDone_other: '{{count}} openings found',
    skipped_one: '{{count}} skipped',
    skipped_other: '{{count}} skipped',
    namedCount_one: '{{count}} named',
    namedCount_other: '{{count}} named',
    openingsNoneNamed: 'None named — blanks are filled in automatically',
    lockedNeedsDetect: 'Find the openings first.',
    lockedNeedsOpenings: 'Name the openings first.',
    lockedUnmatched_one:
      'Clear the {{count}} saved name that no longer applies first.',
    lockedUnmatched_other:
      'Clear the {{count}} saved names that no longer apply first.',
    lockedNeedsSteps: 'Finish steps 1\u20133 first.',
    lockedNeedsStepsBeforeCoolant: 'Finish steps 1\u20132 first.',
    lockedNamesFirst: 'Clear the names that no longer apply first.',
    setGap: 'Set the narrowest gap first.',
    carveDone: 'Fluid volume carved',
    coolantCarveDone: 'Coolant carved',
    detectAgain: 'Detect again',
  },
  blCoverage: {
    suggestedFix: 'Suggested Fix',
    noActionableChanges: 'No actionable changes.',
    applied: 'Applied',
    applyFix: 'Apply Fix',
    gateRelaxed: 'Quality gate relaxed — coverage prioritized',
    gateDefault: 'Default quality gate',
    coverage: 'Coverage',
    coverageWeightedWorst: '{{weighted}} weighted · {{worst}} worst',
    nearWallAspect: 'Near-wall aspect',
    nearWallAspectTooltip:
      'Aspect ratio of the cells snappy actually extruded in the boundary-layer zone. ~10:1 is healthy; 30:1+ means coverage was forced.',
    aspectMax: '{{max}} max',
    aspectMaxP95: '{{max}} max · {{p95}} p95',
    negVolCells: 'Negative-volume cells in BL zone',
    extrudedNothing_one: '{{count}} patch extruded nothing: {{patches}}',
    extrudedNothing_other: '{{count}} patches extruded nothing: {{patches}}',
    appliedTooltip:
      'Suggested overrides applied. Re-submit the mesh to see the effect.',
    applyTooltip:
      'Apply the suggested overrides. Right-click for a before/after preview.',
    noLayersExtruded:
      'Boundary layers were requested but none were extruded — no layers were produced on any wall patch (usually a too-coarse cell size or a difficult surface). The mesh is usable without inflation; reduce the minimum element size or repair the geometry to recover coverage.',
    patchCount_one: '{{count}} patch',
    patchCount_other: '{{count}} patches',
    layerYield: '{{pct}}% layer yield',
    allApplied: 'All applied',
    applyN_one: 'Apply {{count}} fix',
    applyN_other: 'Apply {{count}} fixes',
    allAppliedTooltip:
      'All suggested overrides applied. Re-submit the mesh to see the effect.',
    applyNTooltip_one:
      'Apply suggested overrides for {{count}} patch. Anti-compound: each diff is anchored to the same baseline.',
    applyNTooltip_other:
      'Apply suggested overrides for {{count}} patches. Anti-compound: each diff is anchored to the same baseline.',
    noiseBanner:
      '{{zeroCount}} of {{total}} wall patches show zero coverage — many are likely small CAD features (fillets, edges) that auto-fail. Consider tagging specific faces in the Boundary Layer panel to limit targeting to patches that matter.',
    hoverNeedsTags:
      'Hover needs per-face tags. Open the Boundary Layer panel, click surfaces to tag them, then re-mesh.',
    hoverUnavailable: 'Hover unavailable on this mesh. Re-mesh to enable.',
    layersCount: '{{achieved}}/{{target}} layers',
  },
  boundaryLayer: {
    title: 'Boundary Layers',
    preview: 'Preview',
    presetNone: 'None',
    presetNoneTitle:
      'Disable BL extrusion entirely — useful when sanity-checking a hex mesh without the addLayers stage.',
    presetStandard: 'Standard',
    presetStandardTitle:
      '3-layer default for general CFD. Captures gross BL effects; fast meshing. Use with wall functions in the solver.',
    presetResolved: 'Resolved',
    presetResolvedTitle:
      'Wall-resolved (Y⁺ ≈ 1) for LES / low-Re RANS. Many thin layers, slow mesh + solve, accurate wall shear.',
    diagnosisInProgress: 'Diagnosis in progress',
    diagnosisInProgressBody:
      'STL preview for face selection becomes available after diagnosis completes. Clicking to add wall groups won’t work until then.',
    pickingOnCavity: 'Picking on cavity surfaces',
    pickingOnCavityBody:
      'Internal-flow mode picks faces on the extracted fluid cavity, not the body. Cap faces (inlet/outlet) are individually selectable.',
    cavityNotRendered: 'Cavity STL not yet rendered',
    cavityNotRenderedBody:
      "The cavity STL hasn't painted in the viewer (still loading, or it failed to fetch / parse). Picks made now resolve against the body STL; the backend will translate them to cavity faces at submit time but ambiguous matches may drop. Wait for the cavity to render, or pick anyway and accept the translation risk.",
    picksCleared: 'Previous wall picks cleared',
    picksClearedBody:
      'Wall groups reference face indices from the prior surface source. Re-tag faces on the current surface to continue.',
    dismissNotice: 'Dismiss notice',
    noFluidCavity: 'No flow cavity yet',
    noFluidCavityBody:
      'Boundary layers grow on the cavity walls, and no flow cavity has been extracted from this part yet. Run Extract in Mesh Settings, or switch to external flow.',
    layersGrowInward: 'Layers grow inward into the fluid',
    layersGrowInwardBody:
      'The fluid is modeled in the CAD, so prism layers extrude from the wall/obstacle surfaces into the surrounding fluid — the same inward direction as an extracted cavity, not outward like external flow.',
    description:
      'Prismatic layers near wall surfaces for accurate boundary layer resolution in CFD.',
    sectionLabel: 'Boundary Layers',
    on: 'On',
    off: 'Off',
    wallGroupsSelected: 'Wall groups selected for prism layer inflation.',
    groupsShort: '{{count}} grp',
    triShort: '{{value}} tri',
    areaShort: '{{value}} area',
    layerInputModeTooltip:
      'Two ways to set the first cell height (y₀). Defaults: type y₀, layer count and growth ratio.\nY+ Compute: solve it from a target y⁺ (how far the first cell sits from the wall) and flow speed, length and fluid to cover where the turbulence model needs it.\ny⁺≈1 to resolve the wall, 30–300 for wall functions.',
    layerInputMode: 'Layer Input mode',
    yPlusCompute: 'Y+ Compute',
    defaults: 'Defaults',
    globalDefaults: 'Global Defaults',
    firstCellHeight: 'First Cell Height y₀',
    firstCellHeightYPlusTitle:
      'Driven by y+ calculator below — turn off the y+ switch to edit directly. Unit selector still works and is shared with the calculator readout.',
    firstCellHeightTitle:
      'First-cell wall-normal height for the prism stack. Submit value is mm regardless of the displayed unit. Enter -1 or "auto" to let the solver pick it.',
    autoInitialHeight: 'auto',
    layerCount: 'Layer Count N',
    growthRatio: 'Growth Ratio r',
    totalThickness: 'Total thickness',
    fluidDensity: 'Fluid Density',
    fluidPresetTooltip:
      'Reference fluid for the y+ calculation. Air and Water use their 20 °C / 1 atm kinematic viscosity. Custom ρ lets you enter ν directly.',
    customDensityLabel: 'Custom ρ',
    customNuTitle:
      'Kinematic viscosity ν = μ/ρ. Air ≈ 1.5e-5 m²/s, water ≈ 1.0e-6 m²/s at 20 °C.',
    yPlusTarget: 'y+ Target',
    yPlusTargetPlaceholder: 'e.g. 1',
    yPlusTargetTitle:
      'How close the first mesh layer sits to the wall — y⁺ is a dimensionless wall distance (not an energy term). Use ≈1 to resolve the boundary layer directly (low-Re RANS / LES), or 30–300 when relying on wall functions. We turn your y⁺ into the first cell height using the flow speed, length and fluid you set below.',
    velocity: 'Velocity U∞',
    velocityPlaceholder: 'e.g. 10',
    velocityTitle:
      'Freestream velocity for external flow, bulk velocity for internal flow. Drives Re = U·L/ν.',
    charLength: 'Char. Length L',
    along: 'along',
    charLengthAxisInternalTooltip:
      'Internal flow — pick the axis representing the hydraulic dimension',
    charLengthAxisTooltip: 'Choose the CAD bound to seed L from',
    charLengthTitle:
      'Characteristic length for Re. Defaults to the CAD bounding box along the selected axis (X / Y / Z); type a value to override. Unit is shared with the Enclosure panel.',
    totalThicknessReadout: 'Total thickness =',
    layerCountReadout: 'Layer count N =',
    laminarWarning:
      'Re < {{threshold}} — flow is laminar. The Cf correlation used here is for turbulent flow, so y₀ may be inaccurate; consider whether a wall-resolved BL is needed at this Re.',
    transitionWarning:
      'Transitional Re — Cf correlation accuracy degrades; treat y₀ as an estimate.',
    advancedAddLayers: 'Advanced (snappy addLayers)',
    readOutLengthUnit: 'Read-out length unit',
    carvedWallLayers: 'Carved wall layers',
    wallGroups: 'Wall Groups',
    deleteSelected: 'Delete selected',
    globalActive: 'Global BL active — all groups use global defaults',
    globalPopulate:
      'Populate all geometry faces and apply global defaults uniformly',
    global: 'Global',
    selectAll: 'Select All',
    selectAllTooltip: 'Select all (Ctrl+A)',
    addFromViewer: 'Click a surface in the viewer to add it here.',
    confirmRemove: 'Click to confirm remove',
    clickToSelect: 'Click to select',
    clickToRename: 'Click to rename',
    triCount: '{{value}} tri',
    areaCount: '{{value}} area',
    clickAgainToRemove: 'click again to remove',
    globalRow: 'global',
    applyAndBack: 'Apply & back to mesh',
    unitLabel: 'Unit',
    regimeLaminar: 'Laminar',
    regimeTransition: 'Transition',
    regimeTurbulent: 'Turbulent',
  },
  embeddedFluid: {
    roleFluid: 'Fluid',
    roleWall: 'Wall',
    roleExclude: 'Exclude',
    roleFluidHint: 'Part of the flow domain. At least one body must be fluid.',
    roleWallHint:
      'An obstacle in the flow — its surfaces become no-slip walls of the fluid.',
    roleExcludeHint:
      'Removes the body entirely. Only for parts that do not touch the flow (fasteners, etc.). If it shares a surface with the fluid, use Wall.',
    internalFlowStyle: 'Internal flow style',
    singleBodyHint:
      'This part has a single body, so there is nothing to classify — Embedded Fluid needs a multi-body assembly (fluid volumes plus solid obstacles). Fluid Extraction derives the cavity automatically.',
    fluidExtraction: 'Fluid Extraction',
    embeddedFluid: 'Embedded Fluid',
    stlUnsupported:
      'Embedded fluid needs a multi-body solid CAD file (STEP/BREP). STL isn’t supported — use Fluid Extraction, or re-upload as STEP.',
    embeddedDescription:
      'The fluid is modeled in the CAD. Classify each body as fluid or wall obstacle below; obstacles become no-slip walls.',
    extractionDescription:
      'The fluid cavity is extracted from a closed solid. Tag inlet/outlet faces below.',
    bodies: 'Bodies ({{count}})',
    markOneFluid: 'Mark at least one body as Fluid — the flow needs a domain.',
    bodyLabel: 'Body {{index}}',
  },
  enclosure: {
    hideEnclosure: 'Hide enclosure',
    showEnclosure: 'Show enclosure',
    enclosure: 'Enclosure',
    openGeometry_one:
      'Open geometry ({{count}} boundary edge). Domain placement may be unreliable. For internal flow (ducts, housings), set all factors to 0.',
    openGeometry_other:
      'Open geometry ({{count}} boundary edges). Domain placement may be unreliable. For internal flow (ducts, housings), set all factors to 0.',
    displayInTooltip:
      'Displaying in {{unit}} (CAD source: {{source}}). Right-click to change.',
    unitsInferredTooltip:
      'Units inferred from STEP file header ({{unit}}). Right-click to change display units.',
    noUnitTooltip:
      'No unit found in STEP header — right-click to set display units.',
    displayUnits: 'Display Units',
    fromCad: 'From CAD',
    unknownUnit: 'unknown',
    unitPlaceholder: 'unit?',
    roleUpstream: 'Upstream',
    roleDownstream: 'Downstream',
    roleLateral: 'Lateral',
  },
  snappyFields: {
    rangeSuffix:
      'Default: {{default}} · Strict {{strict}} / Permissive {{permissive}}',
    maxNonOrthoLabel: 'Max Non-Ortho',
    maxNonOrthoDesc:
      'Max face non-orthogonality angle (°). Higher = more cells accepted, lower solver accuracy. Main reliability knob.',
    maxInternalSkewLabel: 'Max Internal Skew',
    maxInternalSkewDesc:
      'Max skew for internal faces. Cells above this are rejected.',
    maxBoundarySkewLabel: 'Max Boundary Skew',
    maxBoundarySkewDesc:
      'Max skew for boundary (wall) faces. Wall cells tolerate more skew.',
    maxConcaveLabel: 'Max Concave',
    maxConcaveDesc: 'Max cell concavity angle (°).',
    minVolRatioLabel: 'Min Vol Ratio',
    minVolRatioDesc:
      'Min volume ratio between adjacent cells. Lower = allows abrupt size jumps.',
    minFaceWeightLabel: 'Min Face Weight',
    minFaceWeightDesc: 'Min interpolation weight — affects FV scheme accuracy.',
    minTwistLabel: 'Min Twist',
    minTwistDesc: 'Min face twist value.',
    minDeterminantLabel: 'Min Determinant',
    minDeterminantDesc: 'Min cell determinant (filters near-degenerate cells).',
    relaxedMaxNonOrthoLabel: 'Relaxed Max Non-Ortho',
    relaxedMaxNonOrthoDesc:
      'Fallback non-ortho cap when the primary threshold fails — snappy retries with this looser bound.',
    maxFaceThicknessRatioLabel: 'Max Face Thickness Ratio',
    maxFaceThicknessRatioDesc:
      'Max layer thickness / adjacent face thickness ratio during BL extrusion. Higher lets BL fit on curved surfaces where face thickness varies.',
    maxThicknessToMedialLabel: 'Max Thickness/Medial',
    maxThicknessToMedialDesc:
      'Max layer thickness / local medial-axis distance.',
    minMedialAxisAngleLabel: 'Min Medial Axis Angle',
    minMedialAxisAngleDesc:
      'Below this angle (degrees) snappy considers the medial axis too pinched and refuses extrusion.',
    nLayerIterLabel: 'N Layer Iter',
    nLayerIterDesc:
      'Max outer iterations of extrude → check quality → relax → retry during addLayers.',
    nRelaxedIterLabel: 'N Relaxed Iter',
    nRelaxedIterDesc:
      'Sub-iterations after switching to relaxed mesh-quality constraints in addLayers.',
  },
  engineOverrides: {
    beta: 'Beta',
    snapToFeatureEdges: 'Snap to CAD feature edges',
    snapToFeatureEdgesHelper:
      'Pulls mesh vertices directly onto the CAD feature curves while meshing — sharper, cleaner edges on most geometries.',
    polishSnappedEdges: 'Polish snapped edges',
    polishSnappedEdgesHelper:
      "After snapping, nudges leftover near-edge vertices onto the CAD curves to smooth out serrated edges. Works best with 'Snap to CAD feature edges' on.",
    engineOverrides: 'Engine Overrides',
    presetFast: 'Fast',
    presetFastTitle:
      'Coarse near-wall, ~30s mesh on typical geometries. First-pass validation or debugging boundary conditions — physics will be inaccurate.',
    presetStandard: 'Standard',
    presetStandardTitle:
      'Case-grounded baseline for typical CAD (feature_angle 120°, n_smooth_patch 5, snap iters 100/10). Refinement levels stay Auto — backend clamps based on geometry complexity.',
    presetFine: 'Fine',
    presetFineTitle:
      'Sharp-feature production CFD — 5–10× more cells. Use on blades, fins, sharp leading edges where near-wall accuracy matters.',
    refineInverted:
      'Refine Level Min ({{min}}) > Refine Level Max ({{max}}). Backend will reject this — flip them.',
    refineMaxHigh:
      'Refine Level Max {{max}} ≈ {{factor}}× wall cells — large L6+ meshes can run tens of minutes and exceed the default 30-min strategy timeout. Raise Strategy Timeout if meshing is slow.',
    snapFidelity: 'Snap Fidelity',
    edgeRecoveryTip:
      'Tip: also turn on “Polish snapped edges” for the cleanest result.',
    snappyQuality: 'Snappy Quality',
    presetAdvanced: 'Advanced',
    presetAdvancedTitle:
      'Tighten every acceptance threshold — research-grade. Best solver accuracy when CAD is clean, but the mesher may reject more cells. Start here only if you have a known-good case.',
    presetRelaxed: 'Relaxed',
    presetRelaxedTitle:
      'Loosen acceptance thresholds — useful when the mesh keeps failing on curved or sharp geometries. Mesh will complete; solver accuracy may suffer.',
    presetReset: 'Reset',
    presetResetTitle:
      'Reset all snappy quality + addLayers knobs to Auto. Backend uses its empirically-validated balanced defaults.',
    cellBudget: 'Cell Budget',
    cellBudgetHint:
      'Defaults scale 2× per refinement level above L4. Pin a value to override.',
    addLayers: 'Add Layers',
    prioritizeBlCoverage: 'Prioritize BL coverage',
    prioritizeBlCoverageHelper:
      'Accept degraded (but valid) cell quality for more boundary-layer coverage. The validated way to relax the layer gate — prefer this over the Snappy Quality “Relaxed” preset. Default off.',
    advancedOverrides: 'Advanced overrides',
    bypassDirtyCad: 'Bypass dirty-CAD pre-flight gates',
    bypassDirtyCadHelper:
      'Lets self-intersecting or non-watertight geometry reach meshing instead of being blocked up front. On by default, so a run always attempts to mesh; untick to re-arm the pre-flight gates and block dirty CAD before it wastes mesher time.',
    fieldSmoothing: 'Smoothing',
    fieldAlgorithm2d: 'Algorithm 2D',
    fieldAlgorithm3d: 'Algorithm 3D',
    fieldGrading: 'Grading',
    fieldOptPasses: 'Opt. Passes',
    fieldCurvatureSafety: 'Curvature Safety',
    fieldSegmentsPerEdge: 'Segments/Edge',
    fieldRefineLevelMin: 'Refine Level Min',
    fieldRefineLevelMax: 'Refine Level Max',
    fieldFeatureAngle: 'Feature Angle',
    fieldCellsBetweenLevels: 'Cells Between Levels',
    fieldResolveFeatureAngle: 'Resolve Feature Angle',
    fieldSmoothPatch: 'Smooth Patch',
    fieldBlockmeshCellSize: 'BlockMesh Cell Size',
    fieldSnapSolveIter: 'Snap Solve Iter',
    fieldFeatureSnapIter: 'Feature Snap Iter',
    fieldSnapTolerance: 'Snap Tolerance',
    fieldMeshSizeFine: 'Mesh Size Fine',
    fieldMeshSizeCoarse: 'Mesh Size Coarse',
    fieldMeshSizeFarfield: 'Mesh Size Farfield',
    fieldMaxLocalCells: 'Max Local Cells',
    fieldMaxGlobalCells: 'Max Global Cells',
    tipSmoothing:
      'Laplacian smoothing passes applied after meshing to lift element quality. Higher is smoother but slower. Default: Auto.',
    tipAlgorithm2d:
      'Gmsh 2D surface meshing algorithm (numeric code — e.g. 6 = Frontal-Delaunay). Leave on Auto unless surface meshing struggles on this part.',
    tipAlgorithm3d:
      'Gmsh 3D volume meshing algorithm (numeric code — e.g. 10 = HXT, a fast parallel Delaunay). Default: Auto.',
    tipGrading:
      'How fast element size grows from fine to coarse regions. Near 1.0 is nearly uniform; higher coarsens faster for fewer cells. Default: Auto.',
    tipOptPasses:
      'Post-mesh optimization passes that untangle and improve poor tetrahedra. More passes mean better quality but slower meshing. Default: Auto.',
    tipCurvatureSafety:
      'How aggressively curved surfaces are refined — higher resolves curvature with smaller elements (more cells). Default: Auto.',
    tipSegmentsPerEdge:
      'Minimum mesh segments along each CAD edge. Raise to resolve short edges and small features. Default: Auto.',
    tipRefineLevelMin:
      'Minimum surface refinement level on the body. Each +1 halves the wall cell size locally. Default: Auto (from min_size). Try 2–3.',
    tipRefineLevelMax:
      'Maximum surface refinement. Each +1 ≈ 8× more cells in that zone. Primary knob for near-wall detail. Default: Auto (from min_size). Try 4–6.',
    tipFeatureAngle:
      'Dihedral angle (°) below which an edge is treated as a sharp feature and refined extra. Lower = more edges captured. Default: 150°. Try 30° (sharp), 60° (mild).',
    tipCellsBetweenLevels:
      'Buffer cells in the transition zone between refinement levels. Higher = smoother grading, fewer skewed cells. Default: 3. Try 3–5.',
    tipResolveFeatureAngle:
      'Snap-phase angle threshold (°) for locking the mesh to a detected feature. Lower = more aggressive corner snapping. Default: 30°. Try 15–30°.',
    tipSmoothPatch:
      'Surface mesh smoothing iterations after snapping. Higher = smoother patches, slower. Default: 3. Try 3–5.',
    tipBlockmeshCellSize:
      'Background hex edge length (mm). Sets the base before refinement; everything else is relative to this. Default: Auto (~body/30). Try 5–10% of body length.',
    tipSnapSolveIter:
      'Solver iterations during the snap phase. Higher = better wall conformance, slower. Default: 30. Try 30–50.',
    tipFeatureSnapIter:
      'Iterations dedicated to snapping mesh to feature edges. Default: 5. Try 5–10 for sharp geometries.',
    tipSnapTolerance:
      'Snap displacement tolerance (× cell size). Lower = tighter snap (more inverted-cell risk); higher = looser snap (worse surface conformity). Default: 2.0. Try 1.0–5.0.',
    tipMeshSizeFine:
      'Wall STL triangle size (mm). Sets how detailed the input surface is before the mesher refines further. Default: ½ × min_size.',
    tipMeshSizeCoarse:
      'STL triangle size away from features (mm). Default: 2× fine. Try 2–5× fine.',
    tipMeshSizeFarfield:
      'Triangle size on the enclosure box, and the background cell size over the domain (mm). The biggest lever on total cell count — coarsen it rather than refine it. Default: Auto (~body/7).',
    tipMaxLocalCells:
      'Max cells each parallel worker holds during refinement. Default scales with Refine Level Max (L{{level}}): {{value}}. Pin a value to override.',
    tipMaxGlobalCells:
      'Total cell-count limit during refinement. Default scales with Refine Level Max (L{{level}}): {{value}}. High-refinement L6 fans can need ≥ 16M. Pin a value to override.',
    autoDerivedSize:
      'Auto resolves to about {{value}} mm here. The mesher may adjust it to fit your geometry.',
    autoDerivedLevel:
      'Auto resolves to about level {{value}} here, based on your mesh sizes. The mesher may adjust it to fit your geometry.',
  },
  generatingMesh: {
    generatingMesh: 'Generating mesh',
  },
  meshExport: {
    export: 'Export',
  },
  refinement: {
    // Post-mesh, quality-driven refinement of an EXISTING mesh (SICN
    // threshold). Named "Adaptive refinement" to distinguish it from the
    // pre-mesh spatial "Local refinement" zones (compMesh.refinementZones).
    localRefinement: 'Adaptive refinement',
    sicnThreshold: 'SICN threshold (refine elements below)',
    targetSize: 'Target size (mm, optional)',
    submitting: 'Submitting…',
    refineMesh: 'Refine mesh',
    failedToSubmit: 'Failed to submit',
  },
  refinementZones: {
    title: 'Local refinement',
    stepSummary_one: '{{count}} zone',
    stepSummary_other: '{{count}} zones',
    entryHint: 'Refine the mesh inside boxes you place in the viewer',
    hexOnly: 'Hex or Poly meshing only',
    description:
      'Place boxes where the flow needs finer cells — a narrow passage, a wake, a jet. Cells inside each zone refine to its target size; the rest of the mesh stays coarse.',
    zonesHeader: 'Zones',
    addBox: 'Add box',
    addSphere: 'Add sphere',
    addWaitingForModel: 'Waiting for the model to load…',
    empty: 'No zones yet. Add a box to refine a region of the domain.',
    zoneSummaryBox: 'Box · {{dims}} mm',
    zoneSummarySphere: 'Sphere · r={{radius}} mm',
    showInViewer: 'Show in viewer',
    hideInViewer: 'Hide in viewer',
    hiddenStillApplies: 'Hidden in the viewer — still applies when you mesh.',
    renderBudgetExceeded:
      'Too many zones to show all at once — the extra ones still apply when you mesh.',
    deleteZone: 'Delete zone',
    renameZone: 'Zone name',
    boxDefinition: 'Box definition',
    sphereDefinition: 'Sphere definition',
    modeCenterSize: 'Center + size',
    modeMinMax: 'Min / max',
    centerMm: 'Center (mm)',
    sizeMm: 'Size (mm)',
    minPointMm: 'Min corner (mm)',
    maxPointMm: 'Max corner (mm)',
    radiusMm: 'Radius (mm)',
    targetCellSize: 'Target cell size',
    centerTip: "Where the box sits, in the model's coordinates.",
    sizeTip: 'How wide the box is along each axis.',
    minPointTip:
      'The corner with the smallest X, Y, Z — marked in amber in the viewer.',
    maxPointTip:
      'The corner with the largest X, Y, Z — marked in cyan in the viewer.',
    radiusTip: 'How far the sphere reaches from its center.',
    targetCellSizeTip:
      'The cell size you want inside this zone. It snaps to the nearest refinement level.',
    refinementLevel: 'Refinement level',
    refinementLevelTip:
      'Each level halves the background cell ({{blockmesh}} mm), so cells refine in powers of two. Your target snaps to the nearest level. Click a level to set the target to its cell size.',
    effectiveOk: 'Effective size {{size}} mm at level {{level}}',
    cappedTitle: 'Target out of reach at level {{level}}',
    cappedDetail:
      'You asked for {{target}} mm, but the max refinement level ({{level}}) only reaches {{effective}} mm here. Raise the level to hit your target.',
    raiseLevel: 'Raise max refinement level to {{level}}',
    beyondMaxLevel:
      'A {{target}} mm target needs level {{level}} — beyond the maximum of 8. Use a coarser target.',
    coarserThanBase:
      'Target is coarser than the background cell ({{blockmesh}} mm), so this zone adds no refinement.',
    sizesUnknown:
      'Cell-size preview needs the geometry bounds — generate a diagnosis first.',
    approxNote:
      'Sizes are estimates — the mesher may adjust them to fit cell budgets.',
    presets: 'Presets',
    fitToDomain: 'Fit to domain',
    fitToFaceHint:
      'click a face in the viewer to wrap the selected zone around it.',
    done: 'Done',
    cancel: 'Cancel',
  },
  meshSource: {
    label: 'Mesh from',
    fluidDomain: 'Fluid domain',
    body: 'Body',
    tip: 'Which surface to view, pick, and mesh from. Fluid domain = the extracted cavity; Body = the CAD surfaces — use when your upload already is the fluid volume.',
  },
  overrideInput: {
    auto: 'Auto',
  },
  qualityTarget: {
    qualityTarget: 'Quality Target',
    off: 'Off',
    offTitle: 'No post-mesh quality gate',
    presetLoose: 'Loose',
    presetStandard: 'Standard',
    presetTight: 'Tight',
    tetLabel: 'Min SICN',
    tetHint:
      'Higher is stricter (0–1). Extra optimizer passes run until the worst element SICN meets the target.',
    tetDescription:
      'Post-mesh quality gate. The tet optimizer runs additional smoothing passes until the minimum element SICN meets the target.',
    hexLabel: 'Max Non-Ortho (°)',
    hexHint:
      'Lower is stricter (degrees). The mesh is re-triggered until max non-orthogonality meets the target.',
    hexDescription:
      'Post-mesh quality gate. snappyHexMesh is re-triggered with tightened controls until max non-orthogonality meets the target.',
    polyDescription:
      'Post-mesh quality gate. cfMesh is re-meshed with tightened controls until max non-orthogonality meets the target.',
  },
  geometryHealth: {
    errorTimeout:
      'Geometry is too complex to analyze. Try simplifying the model or removing small features.',
    errorSegfault:
      "Geometry contains features that couldn't be processed. Check for corrupt surfaces or overlapping bodies.",
    errorOom:
      'Geometry is too large to analyze. Try reducing the number of bodies or splitting the assembly.',
    errorAbort:
      'Geometry format could not be read. Try re-exporting the file from your CAD tool.',
    errorNoResult:
      "Analysis completed but couldn't extract topology data. Try re-exporting the file as STEP.",
    errorZeroVolume:
      'This geometry has no solid volume — it may be a surface body or sketch. Meshing requires a closed solid.',
    errorNoGeometry:
      'No geometry found in file. The file may be empty, contain only points or curves, or use an unsupported format.',
    errorGeneric: 'Diagnosis failed. Try uploading the file again.',
    errorFallback:
      "Geometry could not be analyzed. Try re-exporting from your CAD tool with 'heal geometry' enabled.",
    dimensionalTooltipWithUnit:
      "Original CAD unit detected during diagnosis: {{unit}}. Values shown in mm (OCC's internal working unit).",
    dimensionalTooltipNoUnit:
      "No unit declared in CAD header — values shown in mm (OCC's internal working unit). Pick the source unit in Mesh Settings → Units.",
    countVol: '{{count}} vol',
    countSurf: '{{count}} surf',
    countCurv: '{{count}} curv',
    diagValue: '{{value}} mm diag',
    surfaceAreaValue: 'SA {{value}} mm²',
    manifold: 'Manifold',
    watertight: 'Watertight',
    geometryClean: 'Geometry is clean',
    noIssuesFound: 'No topology or quality issues found',
    blockingTitle: 'Blocking · must fix',
    blockingFootnote:
      'Topology errors — meshing may fail or produce bad cells. Repairing the CAD is recommended.',
    qualityTitle: 'Quality · advisory',
    qualityFootnote: 'Usually addressed by choosing a finer mesh size.',
    shownInViewer: '{{shown}}/{{total}} shown in viewer',
    showAll: 'Show all',
    noPerIssueDetails: 'No per-issue details were returned for this geometry.',
    showInViewer: 'Show in viewer',
    hideInViewer: 'Hide in viewer',
    showLayerInViewer: 'Show {{label}} in viewer',
    hideLayerInViewer: 'Hide {{label}} in viewer',
    askAi: 'Ask AI',
    askAiTooltip: 'Ask AI about this geometry',
    uploadToRunDiagnosis: 'Upload a file to run diagnosis',
    diagnosingGeometry: 'Diagnosing geometry…',
    detailedIncomplete: 'Detailed diagnosis incomplete',
    diagnosisFailed: 'Diagnosis failed',
    detailedIncompleteBody:
      'Health checks didn’t finish, but the geometry preview is available and meshing can still proceed.',
    noDiagnosisAvailable: 'No diagnosis available',
    repairingNewVersion: 'Repairing new version…',
    diagnosingNewVersion: 'Diagnosing new version…',
    uploading: 'Uploading…',
    uploadFixedVersion: 'Upload fixed version',
    title: 'Geometry Health',
  },
  repairHistory: {
    issueManifoldEdge: 'non-manifold edges',
    issueManifoldVertex: 'non-manifold vertices',
    issueWatertight: 'boundary gaps',
    issueWinding: 'inconsistent face winding',
    issueZeroLengthEdge: 'zero-length edges',
    issueSelfIntersection: 'self-intersections',
    issueOverlappingFace: 'overlapping faces',
    issueDegenerateFace: 'degenerate faces',
    fullyRepaired: 'Fully repaired',
    partiallyRepaired: 'Partially repaired',
    repairFailed: 'Repair failed',
    // UNREPAIRABLE: strategies ran, but a defect none can fix survived, so the
    // output was discarded (ENG-2490). Distinct from TERMINAL, where nothing ran.
    repairNotKept: 'Not kept',
    repairAttempted: 'Repair attempted',
    fixed: 'Fixed {{labels}}',
    // `items` is a comma-joined list of "<defect label> (<count>)" entries.
    removed: 'Removed: {{items}}',
    diagnosing: 'Diagnosing...',
    repairing: 'Repairing...',
    current: '(current)',
    viewVersion: 'View version {{version}}',
    viewVersionCurrent: 'View version {{version}} (current)',
    downloadVersion: 'Download version {{version}} source file',
    downloadTooltip: "Download this version's source file",
    title: 'Repair History ({{count}} versions)',
    // Geometry-version origin badges. English strings live in the source
    // of truth `geometryOrigin.ts::GEOMETRY_ORIGIN_CONFIG`; these i18n
    // entries drive the display via `geometryVersionBadge(version, t)`.
    originUpload: 'Upload',
    originAutoRepair: 'Auto repair',
    originManualRepair: 'Repair',
    originManualUpload: 'Manual upload',
    originCadMorph: 'CadMorph',
    failureCadMorphRejected: 'CAD change rejected',
    originCadSimplify: 'CAD simplify',
    // Terminal-error badges — surfaced when a version's status is 'error',
    // distinguished by which operation failed (rejected simplify / failed
    // auto-repair / failed diagnosis).
    failureSimplifyRejected: 'Simplify rejected',
    failureRepairFailed: 'Repair failed',
    failureDiagnosisFailed: 'Diagnosis failed',
  },
  snapFidelity: {
    surfaceDev: 'Surface dev (mean / max)',
    surfaceDevHint:
      'How far the meshed surface sits from the CAD surface — average and worst-case distance. Smaller means tighter adherence to the original geometry.',
    featureCapture: 'Feature capture (≤ 0.5 cell)',
    featureCaptureHint:
      'Share of CAD feature edges that landed within half a cell width of their true position. Higher means sharp edges snapped where they should.',
    resolvableFeatures: 'Resolvable features',
    resolvableFeaturesHint:
      'Fraction of CAD feature edges the current cell size is fine enough to resolve at all. Low values mean the mesh is too coarse to snap them — reduce the minimum element size first.',
    snapConverged: 'Snap converged',
    snapConvergedHint:
      'Whether the mesher’s edge-snapping iterations settled. “No” means some edges never locked onto the CAD features; any leftover illegal faces are shown alongside.',
  },
  submitButton: {
    blockedTooltip:
      'One or more configuration errors block submission. Resolve the items flagged above.',
    fixValidationErrors: 'Fix validation errors above',
    preparing: 'Preparing…',
    generateMesh: 'Generate Mesh',
  },
  validation: {
    // ENG-1908. The terse blocker; the card at the size field carries the WHY.
    // No "or clear it" remedy: the Min Size box cannot be emptied — an
    // unparseable value is reset on blur — so offering that is an instruction
    // the user cannot follow.
    cellCoarserThanPassage:
      'Use {{target}} mm or smaller — {{minSize}} mm is too coarse for the flow passage.',
    chtNeedsCavity:
      'Extract the coolant cavity first — a conjugate run needs the fluid as its own body.',
    chtNeedsFluidBody:
      'Mark the coolant body as the fluid in Body Classification.',
    noFluidCavityWarning:
      'No fluid cavity was extracted for this geometry (often a dirty or unrepaired body). Repair the body and re-run diagnosis, or switch to external flow, for a reliable cavity mesh.',
    noWrapCavity:
      'No flow cavity has been carved from this part yet. Run the fluid extraction step, then submit the mesh.',
    // ENG-3072. Wrap CHT with inside cooling: no body to classify, unlike
    // Repair's `chtNeedsCavity`.
    noWrapChtCarve:
      'Carve the coolant before meshing. Open Boundary Assignment, find the openings, then carve.',
    embeddedNeedsHex:
      'Embedded Fluid needs a hex mesh — a tetrahedral embedded-fluid run is rejected by the solver. Switch Element Type to Hex.',
    pickUnit:
      'Pick a unit for this geometry — no CAD bounds available yet. Use the Units menu above once diagnosis completes.',
    seedPointRequired:
      'An STL internal-flow mesh needs a seed point inside the fluid cavity — the mesher can’t find it automatically from an STL. Set it in Boundary Assignment.',
    // ENG-1537. Both are guaranteed backend rejections, so they block submit
    // rather than being discovered on a round trip.
    chtPassageRequired:
      'Enter the narrowest passage width in Boundary Assignment to mesh a part cooled from the inside — finding the openings fills it in. The extractor can’t guess it, and a passage finer than the cells gets sealed shut.',
    // ENG-1646. Same field, same reason, different mode — so the "greater than
    // 0" message below stays shared and only the ask-for-it wording splits.
    flowPassageRequired:
      'Enter the narrowest passage width so the flow cavity can be cut out of this part — the extractor can’t guess it, and a passage finer than the cells gets sealed shut.',
    passagePositive: 'Narrowest passage must be greater than 0 mm.',
    // ENG-3052. Region names: what a wrap mesh will call its parts, and a
    // blocker for a name the backend refuses, on both preps.
    partsWillBeNamed: 'Parts will be named {{names}}.',
    partCheckRunning:
      'Still checking this upload for separate parts. Mesh now and they are named part_0, part_1 and so on, or wait and name them yourself.',
    fixPartNames: 'Fix these names in Body Classification: {{names}}.',
    // How the blocker above says a blank name, which `“”` would hide.
    blankName: 'a blank name',
    estCells: 'Est.',
    cellsMeshTime: 'cells ·',
    meshTimeSuffix: 'mesh time',
    warningCount_one: '{{count}} warning',
    warningCount_other: '{{count}} warnings',
    hintCount_one: '{{count}} hint',
    hintCount_other: '{{count}} hints',
    jumpToInput: 'Click to jump to the related input',
    refineMinHigherThanMax:
      'Refine Level Min ({{min}}) is higher than Refine Level Max ({{max}}). Lower the Min or raise the Max.',
    internalFlowEnclosureConflict:
      "Internal Flow and External Enclosure can't both be on. Turn off the enclosure, or switch flow type to External.",
    meshSizeFineTooLarge:
      'Fine Wall STL Size ({{fine}}) needs to be smaller than Max Size ({{max}}).',
    relaxedNonOrthoLowerThanMain:
      "Relaxed Max Non-Ortho ({{relaxed}}) can't be lower than Max Non-Ortho ({{main}}).",
    hexOnlyFirstOrder:
      'Hex meshes only support first-order elements — set Element Order to 1.',
    externalStlNoTet:
      "External-flow meshing on an STL surface can't use tet — set Element Type to Hex or Poly.",
    wrapExternalHexOnly:
      'External-flow meshing on a wrapped surface is hex-only — set Element Type to Hex.',
    // ENG-1465: CHT's element type is forced by the PREP, so the two rows point
    // opposite ways. Reusing the external-flow strings above told a repair-CHT
    // user to switch to Hex, which is exactly backwards.
    wrapChtHexOnly:
      'Conjugate heat transfer on a wrapped part is hex-only — set Element Type to Hex.',
    repairChtTetOnly:
      'Conjugate heat transfer on a repaired part is tet-only — set Element Type to Tet.',
    refineMaxHigh:
      'Refine Level Max {{max}} means roughly {{factor}}× the wall cell count. Set a max cell count, or expect long mesh times.',
    cellsBetweenLevelsLow:
      'Abrupt refinement transitions produce skewed cells. Use at least 3 cells between levels.',
    featureAngleLow:
      'A very low feature angle over-refines smooth surfaces. Typical range is 30–60°.',
    maxNonOrthoHigh:
      'Above 80° the solver often diverges — keep Max Non-Ortho at or below 80.',
    relaxedNonOrthoHigh:
      'Above 85° the pressure solver tends to break down — keep this at or below 85.',
    blGrowthRateHigh:
      'Growth Rate above 1.5 makes the transition from boundary layer to bulk mesh abrupt.',
    blThickStackLowGrowth:
      'A thick layer stack with low growth may not fit between the wall and the refinement zone — try fewer layers or higher growth.',
    blStackTallerThanMinSize:
      'Boundary-layer stack ({{stack}} mm) is taller than Min Size ({{minSize}} mm) — layers will overflow into the bulk mesh. Reduce Layer Count, lower First Layer Height, or coarsen Min Size.',
    blStackVsOpeningDiameter:
      "Boundary-layer stack ({{stack}} mm) is more than 10 % of the smallest opening diameter ({{diameter}} mm) — opposing walls' layers will collide at concave corners. Reduce Layer Count or First Layer Height.",
    blFirstLayerLargerThanMinSize:
      "First Layer Height ({{height}} mm) is larger than Min Size ({{minSize}} mm) — each layer is bigger than the surrounding cells, so there's no boundary-layer resolution.",
    blFirstLayerFarSmallerThanMinSize:
      'First Layer Height is less than 0.1 % of Min Size — that gap needs unrealistic refinement to resolve.',
    blSingleLayer: "Only 1 layer — that's effectively no boundary layer.",
    blManyLayers:
      '{{count}} boundary-layer layers is unusual — more than 8 is rarely needed.',
    blGrowthRateLow:
      "Growth Rate below 1.05 produces near-uniform layers — you'd need many of them to reach the bulk cell size.",
    blSliverTargets_one:
      '{{count}} boundary-layer target ({{labels}}) has surface area below {{threshold}} mm². It is too small to mesh cleanly — the mesher will likely drop it and tear nearby boundary layers.',
    blSliverTargets_other:
      '{{count}} boundary-layer targets ({{labels}}) have surface area below {{threshold}} mm². These are too small to mesh cleanly — the mesher will likely drop them and tear nearby boundary layers.',
    maxInternalSkewLow:
      'Max Internal Skewness at 3 or below often rejects valid boundary-layer cells after extrusion. Try raising to 4 (the default).',
    refineMaxHighOnDenseCad:
      'Refine Level Max 5 or above on a {{surfaces}}-surface CAD produces low-quality cells with many faces. Lower Max Refinement Level to 4.',
    cellsBetweenLevelsHighDeepRefine:
      'Wide transition zones (3 or more Cells Between Levels) combined with deep refinement (Max 5 or higher) compound into more low-quality cells. Lower one or the other.',
    tightFeatureManySnapIters:
      'A tight Feature Angle (≤ 60°) with many snap iterations (≥ 15) pulls vertices too aggressively toward edges. Boundary layers on mirrored faces can come out asymmetric.',
    minMedialAxisAngleHigh:
      'Min Medial Axis Angle at 90° or above can reject boundary layers on moderately-curved walls with many targets. Try lowering to 30°.',
    tetFailsOnDenseCad:
      'Tet meshing often fails on dense CAD (more than 100 surfaces) with per-face boundary layers and silently falls back to hex. Set Element Type to hex up front.',
    blHostileOverridesCombo:
      '{{count}} overrides hostile to boundary layers are set together — high risk of low-quality cells and layer failures. Reset Engine Overrides to Standard and tune one at a time.',
    sizeRatioHigh:
      "Max Size is {{ratio}}× Min Size — that's a steep size jump the mesher may struggle with. Bring Max closer to Min, or expect longer mesh times.",
    sizeRatioLow:
      "Max Size is close to Min Size — refinement won't have much effect. Consider widening the range.",
    blIgnoredNonCfd:
      'Boundary layers apply to CFD and wrap CHT — this job will not include them.',
    refinementAutoDerives:
      'Refinement will auto-derive from Min Size / Max Size — leave overrides on Auto unless you have a specific target.',
    allMeshSizesManual:
      'All Mesh Size overrides set manually — this bypasses Min Size–based auto-derivation.',
  },
  meshSettings: {
    wrapChtLayerDescription:
      'Automatic follows the server default, which may leave layers off. Choose Custom to request layers on all fluid-to-solid contacts.',
    partCheckRefused: 'Could not keep this part separate.',
    partCheckOneBlock:
      'These parts could not all be kept separate. The mesh will use one solid block.',
    partCheckOutside:
      'For outside cooling, these parts are solved as one solid block. Separate parts for outside cooling are not supported yet.',
    partCheckUnavailable:
      'Part separation has not been verified for this upload.',
    partCheckCount: 'Parts found: {{count}}',
    // ENG-3052: inside cooling on an upload whose part check never ran. Only
    // Body Classification shows it (ENG-3065).
    partCheckNever:
      'This upload was never checked for separate parts. Upload the CAD again to name its parts; without names they are called part_0, part_1 and so on.',
    layersSupportedModes: 'CFD or wrap CHT only',
    blCustom: 'Custom',
    blAutomatic: 'Automatic',
    feaCoupling: 'Assembly interfaces',
    feaShared: 'Shared nodes — bonded',
    feaIndependent: 'Independent bodies — contact-ready',
    feaCouplingHint:
      'Shared nodes enforce continuity. Independent bodies require explicit connections in SimOps. Changing this choice requires remeshing; it does not assign contact automatically.',

    title: 'Mesh Settings',
    simulation: 'Simulation',
    prepProvenanceRepair: 'Prep: Repair · set at import',
    prepProvenanceWrap: 'Prep: Wrap · set at import',
    comingSoon: 'Coming soon',
    chtNeedsSolidCad:
      'CHT needs a solid CAD file (STEP/BREP) with separate solid regions — STL isn’t supported. Re-upload as STEP to run CHT.',
    wrapChtScope:
      'Cooling: {{cooling}} (chosen in General Settings). Wrap sets each part’s role itself.',
    // The routing choice itself (ENG-1537). Deliberately phrased as what the
    // part IS, not which derivation runs: the backend calls these
    // external_ambient / internal_fluid, and neither word belongs on a button.
    chtCooling: 'Cooling',
    chtCoolingOutside: 'Outside',
    chtCoolingInternal: 'Inside a channel',
    chtCoolingOutsideTooltip:
      'The part is cooled by the fluid around it — a heatsink in air, or a case with airflow over it. The part becomes one solid and everything inside the enclosure around it becomes the fluid.',
    chtCoolingInternalTooltip:
      'Coolant runs through a passage inside the part — a cold plate or a liquid-cooled block. The passage is found and meshed as the fluid; the metal around it becomes the solid. You find its openings and carve it out in Boundary Assignment.',
    // Shared by wrap CHT and wrap internal flow (ENG-1646) — the label and the
    // unit say nothing mode-specific, so only the hint below is per-mode.
    passage: 'Narrowest passage',
    stepNeedsCavity:
      'Carve the flow cavity before continuing — the later steps work on the carved surfaces.',
    stepNeedsCoolantCarve:
      'Carve the coolant before continuing — Boundary Layers and Generate Mesh wait for it.',
    applySuggestedSize: 'Use suggested size',
    // A unit, not prose — but it still renders to the user, and every other
    // placeholder in this panel is localized (`overrideInput.auto`). Locales that
    // write units differently get to.
    passagePlaceholder: 'mm',
    chtPassageHint:
      'The tightest gap the coolant flows through, in millimetres. Measure the smallest hole or slot, not the main channel — anything finer than this can be sealed shut and dropped from the flow.',
    flowPassageHint:
      'The tightest gap the flow squeezes through, in millimetres. Measure the smallest hole or slot, not the main channel — anything finer than this can be sealed shut and dropped from the flow. A smaller number makes the extraction finer, and slower.',
    // ENG-1644 — the extraction trigger. Deliberately says nothing about voxels
    // or isosurfaces: what the user is buying is a cavity to mesh, and how it is
    // computed belongs in the docs.
    flowCavityTitle: 'Flow cavity',
    // ENG-1885, step 1. "Detect" not "Probe": the user is asking us to look at
    // the part, and "probe" is the backend's word for the job that does it.
    flowCavityDetect: 'Detect',
    flowCavityDetecting: 'Checking',
    flowCavityDetectInFlight:
      'Looking for the openings in this part. About 15 seconds.',
    flowCavityNeedsDetect:
      'Check the part for openings first — it takes about 15 seconds and fills in the narrowest passage below.',
    // Cells, memory and time. Only the cell count is exact; the other two come
    // from a handful of timed runs, so the copy says "about" and means it.
    flowCavityCost:
      'About {{cells}}M cells, {{gb}} GB, {{minutes}} min at this size.',
    flowCavityPassageFrom_one:
      'Narrowest passage {{value}} mm, measured from {{count}} opening.',
    flowCavityPassageFrom_other:
      'Narrowest passage {{value}} mm, measured from {{count}} openings.',
    // The measurement we cannot honour — a different thing from a confident
    // suggestion, and the user should be told which one they are looking at.
    flowCavityPassageClamped:
      'Measured {{detected}} mm, but that needs more cells than the limit allows — filled in {{suggested}} mm instead.',
    flowCavityTooFine:
      'Too fine for this part — the extraction grid would be over the limit. Use {{min}} mm or larger.',
    // "than {{set}}" not "than this": the seal boundary is the value in the box,
    // not the measured one, and "this" reads as the measurement — which
    // understates the risk whenever the set value is much larger.
    flowCavityCoarserThanDetected:
      'Coarser than the {{detected}} mm we measured. Passages finer than {{set}} mm may be closed off.',
    flowCavityExtract: 'Extract',
    flowCavityReExtract: 'Re-extract',
    flowCavityExtracting: 'Extracting',
    flowCavityInFlight:
      'Carving the flow cavity out of this part. This takes a few minutes — you can leave this page and come back.',
    flowCavityNeedsPassage:
      'Set the narrowest passage above first — without it there is no way to tell which openings to keep.',
    flowCavityNameBlank:
      'One face is waiting for a name. Fill it in, or remove the row — look in the wall names above.',
    flowCavityNameRefused:
      'The mesher won’t accept the name “{{name}}”. Rename that face — look in the wall names above and the surface list below.',
    flowCavityUnknown:
      'Couldn’t check whether a flow cavity exists. Reload the page and try again.',
    flowCavityChecking: 'Checking for a flow cavity\u2026',
    flowCavityReady: 'Flow cavity ready. The mesh will be built on it.',
    // Short on purpose: the reason it went out of date is the submit blocker's
    // sentence, rendered verbatim there. Saying it twice on one screen is noise.
    flowCavityStale:
      'The flow cavity no longer matches these settings. Extract it again.',
    flowCavityAbsent:
      'No flow cavity yet. Extract one before generating the mesh — without it the mesh would cover the outside of the part.',
    // The POST being refused, which is a different event from a carve that was
    // accepted and then died in a worker — hence the second key below.
    flowCavityFailed:
      'Couldn’t start the extraction. Check the passage size, then try again.',
    // ENG-1886. Deliberately says nothing about WHY: the extractor's own sentence
    // is rendered verbatim underneath, and it already names the value to use
    // instead. This line exists only so the reason has something to be under.
    flowCavityDetectFailed:
      'Couldn’t check the part for openings. Reload the page and try again.',
    flowCavityRunFailed: 'The last extraction didn’t finish. What stopped it:',
    flowCavityTryAgain: 'Try again',
    // Unreachable today: Wrap serves both CFD flow types since ENG-1643. Kept
    // because the gate that renders it is a live query against the mode matrix,
    // so it speaks again if that matrix ever narrows — hence wording that does
    // not name a specific reason.
    wrapBlockedInternal:
      'This part was imported with Wrap prep, which can’t mesh this flow type. Re-import the file with Repair prep to use it.',
    wrapBlockedFea:
      'This part was imported with Wrap prep, which merges it into a single closed surface — FEA needs the original CAD faces to set boundary conditions. Re-import the file with Repair prep to run FEA.',
    cantMeshSim: 'Can’t mesh {{type}}: {{reason}}.',
    canStillMeshSim:
      '{{reason}}. You can still mesh {{type}}, but it may produce degraded cells.',
    flowType: 'Flow Type',
    internalNeedsCavity: '{{reason}}. You can also switch to external flow.',
    chtInternalTooltip:
      'The coolant runs inside the part. Extract it below, then mark it as the fluid body.',
    chtExternalNotYet:
      'Cooling by the air around the part is not available yet on this prep.',
    cantMeshExternal: 'Can’t mesh external flow: {{reason}}.',
    canStillMeshExternal:
      '{{reason}}. You can still mesh external flow, but the wind-tunnel cut may be unclean and results degraded.',
    external: 'External',
    internal: 'Internal',
    element: 'Element',
    geometryPrep: 'Geometry prep',
    geoPrepRepairTooltip:
      'Diagnoses and repairs the CAD before meshing — fixes gaps, non-manifold edges, and bad faces. The default.',
    geoPrepWrapTooltip:
      'Wraps the part in a clean watertight surface, then meshes that instead — more robust on dirty or leaky CAD. Best for checking whether a part meshes at all; per-face boundary tags are not carried through yet.',
    repair: 'Repair',
    wrap: 'Wrap',
    preset: 'Preset',
    presetOptionCoarse: 'Coarse',
    presetOptionMedium: 'Medium',
    presetOptionFine: 'Fine',
    presetOptionVeryFine: 'Very Fine',
    presetOptionUltraFine: 'Ultra Fine',
    presetOptionCustom: 'Custom',
    unitsDetectedTooltip:
      'Geometry was authored in {{unit}} (detected from the CAD header). Backend rescales the mesh to SI metres at solver time.',
    unitsPickTooltip:
      'Pick the unit the geometry was authored in. Backend rescales the mesh to SI metres at solver time. STL/OBJ files carry no unit header — picking is required.',
    units: 'Units',
    unitPlaceholder: 'Pick…',
    maxSize: 'Max Size',
    minSizePlaceholder: '≥1e-7',
    minSize: 'Min Size',
    meshTooCoarseHex:
      ' Hex mesh will capture the wrong flow region — reduce min size or switch to tet.',
    meshTooCoarseTet:
      ' Tet mesh may crash or produce degenerate elements at the narrow channel.',
    // The wrap arm of the too-coarse card. Quotes the exact target because it
    // is computed from the same two constants the backend gate uses, so the
    // number here and the number in a refusal cannot disagree.
    wrapMeshTooCoarse:
      'Cell size {{minSize}} mm will close off the {{passage}} mm flow passage — you would get a clean mesh of only part of the flow. Use {{target}} mm or smaller.',
    // ENG-3072. Wrap CHT: a warning only, so "can", and no refusal promised.
    wrapChtMeshTooCoarse:
      'Cell size {{minSize}} mm can close off the {{passage}} mm coolant passage — the mesh would then hold only part of the coolant, with no error. Use {{target}} mm or smaller.',
    meshTooCoarse:
      'Min size ({{minSize}} mm) exceeds the smallest channel radius ({{radius}} mm).{{advice}}',
    boundaryAssignment: 'Boundary Assignment',
    // Stands in for the withheld face-tag entry on wrap + internal flow. It used
    // to end "so this step is skipped", which ENG-1906 made false by giving the
    // names somewhere to be saved. Keeps the half that was always true —
    // boundary layers were never affected — and points at the new control.
    openingNames: {
      heading: 'Name openings before carving',
      intro_one:
        'Name this detected opening before carving, or mark it as not an opening. Blank names are filled in automatically.',
      intro_other:
        'Name the {{count}} detected openings before carving, or mark the ones that are not openings. Blank names are filled in automatically.',
      across: '{{mm}} mm across',
      nameFor: 'Name for {{name}}',
      unnamed: 'Opening {{index}}',
      locate: 'Opening {{name}} — focus or hover to show it in the viewer',
      detectFirst: 'Detect openings first.',
      empty: 'Detection found no openings on this part.',
      ambiguous:
        'Some mouths have the same geometric identity and cannot be named safely.',
      unmatchedSummary_one:
        '1 saved name or mark no longer matches this part. Clear it before carving.',
      unmatchedSummary_other:
        '{{count}} saved names or marks no longer match this part. Clear them before carving.',
      clearAll: 'Clear all',
      ignore: 'Mark {{name}} as not an opening',
      restore: 'Treat {{name}} as an opening again',
      ignoredPlaceholder: 'Not an opening',
      cappedCount_one: '1 opening will be sealed before the carve.',
      cappedCount_other: '{{count}} openings will be sealed before the carve.',
      allIgnored:
        'Every mouth is ignored, so there is no opening left to carve. Restore at least one.',
      carve: 'Carve with these names',
    },
    wallNames: {
      heading: 'Name the walls',
      intro:
        'Pick a face in the viewer to name part of the channel wall — a hot side, a cold side. Unnamed faces all become one wall.',
      empty:
        'No faces picked. Ctrl+click a face in the viewer to name that part of the wall.',
      face: 'F{{index}}',
      wasAnOpening: 'Was a detected opening — name it, or leave as wall',
      nameFor: 'Name for CAD face {{face}}',
      remove: 'Stop naming {{name}}',
      refusal: {
        blank: 'Give this face a name, or remove it.',
        noValidCharacters:
          'Use letters, digits or underscores — this name has none.',
        reserved: 'The mesher uses this name itself. Pick another.',
        interfaceInfix: '"_to_" is reserved for the joins between regions.',
        autoAssigned:
          'Names starting with "opening_" are assigned automatically.',
        collidesWithOpening:
          'An opening already has this name. Walls and openings cannot share one.',
        duplicateName:
          'Two faces would become the same patch. Spaces and underscores count as the same character.',
      },
    },
    namedSurfaces: 'Named Surfaces',
    structuralBodies: 'Body Assignment',
    bodyClassification: 'Body Classification',
    // ENG-3065: the wrap CHT row's status, after the cooling choice.
    bodyClassificationParts_one: '{{count}} part',
    bodyClassificationParts_other: '{{count}} parts',
    boundaryLayers: 'Boundary Layers',
    blOn: 'On · {{count}} layers',
    blOff: 'Off',
    advanced: 'Advanced',
    order: 'Order',
    orderTet: 'Linear (Tet4)',
    orderTetQuadratic: 'Quadratic (Tet10)',
    orderHex: 'Linear (Hex8)',
    orderPoly: 'Linear (Poly)',
    // Tooltips carry the English vocab (Tet = Tetrahedral, Hex =
    // Hexahedral) so the technical term is discoverable in any locale.
    elementTetTooltip:
      'Tet (Tetrahedral): 4-noded pyramidal elements. Robust on dirty CAD; meshed with gmsh.',
    elementHexTooltip:
      'Hex (Hexahedral): 8-noded box elements. Higher accuracy per cell; meshed with snappyHexMesh.',
    elementPolyTooltip:
      'Poly (Polyhedral): arbitrary-sided cells. Robust internal-flow meshing at lower cell counts; meshed with cfMesh pMesh.',
    strategy: 'Strategy',
    auto: 'Auto',
    strategyTimeout: 'Strategy Timeout (s)',
    strategyTimeoutHint:
      'Max time each meshing strategy can run before giving up. Blank uses the default.',
    generalSettings: 'General Settings',
    stepContinue: 'Continue to {{title}}',
    stepFinish: 'Finish setup',
    stepDone: 'Done',
    stepsProgress: '{{done}}/{{total}} steps',
    stepLockedHint: 'Finish step {{n}}: {{title}} first.',
    stepCtaBlockedHint: 'Finish step {{n}}: {{title}}.',
    cfdOnly: 'CFD only',
    notAvailable: 'Not available here',
  },
  partialMesh: {
    // Warning surfaced next to a selectable failed mesh in SimOps
    // (partialMeshWarning). The remediation title prefixes this when
    // available; both strings render as one sentence.
    meshingDidNotFinish: 'Meshing did not finish.',
    usableStillWritten:
      'A usable mesh was still written — you can run it, but the result may be less reliable than a clean mesh.',
  },
  meshFailure: {
    // Per-MeshErrorCode remediation copy (see meshFailureRemediation.ts).
    // Second person, active, present; engineering domain terms only.
    subcellOpening: {
      title: 'An opening is smaller than one mesh cell.',
      hint: 'Refine the mesh near it so it resolves as an opening, or accept it meshed as a wall.',
    },
    openingDidNotSurvive: {
      title: "A tagged opening didn't survive meshing.",
      hint: 'The mesher merged its faces into the surrounding surface, so the patch was lost. Turn on "Snap to CAD feature edges" (Engine Overrides → Snap Fidelity) to hold the opening\'s edges, or refine the mesh near it — then re-run.',
    },
    fluidDomainDisconnected: {
      title: "The fluid region isn't fully connected.",
      hint: 'Switch to Fluid Extraction, or repair the body so the flow path is sealed.',
    },
    cellCapExceeded: {
      title: 'The mesh hit its cell limit before finishing.',
      hint: 'Increase the element size or shrink the domain to lower the cell count.',
    },
    snapFailed: {
      title: "The mesh couldn't snap to the CAD surface.",
      hint: 'Repair the CAD, or loosen the snap settings, then re-run.',
    },
    layerFailed: {
      title: "Boundary layers couldn't grow on some walls.",
      hint: 'Lower the layer count or first-layer height, or turn on Prioritize BL coverage, then re-run.',
    },
    // Deliberately does not lead with "refine" (ENG-1429). The measured cause
    // was a dirty cavity surface shattering the cell graph at baffle
    // insertion, which a finer mesh reduces but does not fix — mirrors the
    // BE's own `MESH_ERROR_REMEDIATION` note for this code.
    domainCoverageLost: {
      title: 'Most of the flow domain was discarded during meshing.',
      hint: 'The mesher could only reach part of the cavity from the interior seed point — usually a surface defect (self-intersections or coincident faces) splitting it, not a cell-size problem. Re-run geometry diagnosis and repair the cavity if it reports defects; a smaller minimum cell size helps only if the passage is genuinely narrow.',
    },
    // ENG-3052, in the backend's own words: no cell size fixes this one.
    chtPartLabelsMismatch: {
      title: 'A part name no longer points at its part.',
      hint: 'Check that each name is on the right part. If they are, upload the CAD again so its parts are checked again, then name them and re-mesh. Meshing without names keeps part_0, part_1 and so on.',
    },
  },
  // Human labels for backend-emitted `quality_warnings[]` codes. English
  // stays the source of truth in `qualityMetrics.ts::QUALITY_WARNING_LABEL`;
  // this block drives `formatQualityWarning(code, t)`. Unknown codes fall
  // through to the raw code so a new BE code renders visibly instead of
  // blanking while translations catch up.
  qualityWarningCodes: {
    invertedCells: 'Inverted cells present',
    highNonOrtho: 'High non-orthogonality',
    highSkewness: 'High skewness',
    extremeAspect: 'Extreme aspect ratio cells',
    snapTornWalls: 'Snap-torn walls detected',
    degenerateCells: 'Near-zero-volume cells, likely from a sliver body',
  },
  meshResults: {
    qualityWarnings: 'Quality warnings',
    wedgeCellsTitle:
      'Prism (wedge) cells from snappyHexMesh addLayers — boundary-layer prisms.',
    pyramidCellsTitle:
      'Transition cells between refinement levels. Typically <1 % of total.',
    bulkCellsTitle:
      'Bulk volume cells = total − wedge. May include a small number of pyramid transition cells (typically <1 %). Load the mesh in the viewer to see an exact per-type breakdown.',
    wedgeCellsFallbackTitle:
      'Prism cells produced by snappyHexMesh addLayers. Zero means BL extrusion failed.',
    elements: 'Elements',
    hexCells: 'Hex Cells',
    tetCells: 'Tet Cells',
    wedgeCells: 'Wedge Cells',
    pyramidCells: 'Pyramid Cells',
    polyCells: 'Polyhedral Cells',
    polyCellsTitle:
      'Arbitrary polyhedra (cfMesh pMesh cells, or snappyHexMesh split-hex), rendered natively as their real shape.',
    copiedToast: 'Mesh params copied',
    storageWriteFailed: 'Storage write failed',
    noCopiedToast: 'No copied mesh params — click Copy on a mesh first',
    corruptToast: 'Stored mesh params are corrupt',
    notMeshParamsToast: 'Stored value is not mesh params',
    appliedToast: 'Applied mesh params',
    strategy: 'Strategy',
    element: 'Element',
    maxSize: 'Max Size',
    minSize: 'Min Size',
    order: 'Order',
    boundaryLayers: 'Boundary Layers',
    boundaryLayersValue: '{{value}} layers',
    copy: 'Copy',
    copyTooltip:
      "Save this mesh's params to browser storage so you can re-apply them to a different mesh.",
    apply: 'Apply',
    applyTooltip:
      'Apply the most recently copied mesh params to Mesh Settings.',
    cancelled: 'Cancelled',
    failedAt: 'Failed at {{stage}}',
    failed: 'Failed',
    stageSuffix: 'stopped at {{stage}}',
    lastLogLabel: 'Last log line:',
    meshGenerated: 'Mesh generated',
    queuedFor: 'Queued for {{time}}',
    bypassActive: 'Bypass active: input gate skipped{{detail}}.',
    bypassRecovery: 'Mesh produced via auto-recovery where applicable.',
    dirtyCadHintTitle: 'This gate is optional.',
    dirtyCadHintBody:
      'Turn on Advanced overrides → Bypass dirty-CAD pre-flight gates to try meshing anyway, or re-upload the geometry as a mesh file (STL / OBJ) with the Wrap option to reconstruct a clean CAD.',
    needsWrapTitle: 'This part needs Wrap prep.',
    needsWrapBody:
      'The enclosure cut can’t fit this geometry as-is. Re-import it with Wrap prep to seal it into one watertight surface.',
    reimportWithWrap: 'Re-import with Wrap',
    wireballRecovered:
      'Mesh auto-recovered from wireball via alpha-wrap envelope.',
    namedSurfacesLost: 'Named surfaces were lost.',
    namedSurfacesLostBody:
      'PyTetwild (last-resort fallback) fuses all bodies into a single tet mesh and cannot preserve face labels. Re-mesh with HXT or Delaunay to get named surfaces.',
    inputSettings: 'Input Settings',
    errorDetails: 'Error Details',
    pipelineStages: 'Pipeline Stages',
    qualitySummary: 'Quality Summary',
    na: 'N/A',
    snapFidelity: 'Snap Fidelity',
    boundaryLayerCoverage: 'Boundary Layer Coverage',
    metricMin: 'Min',
    metricMax: 'Max',
    metricAvg: 'Avg',
    metricStd: 'Std',
    metricCount: 'Count',
    metricTarget: 'Target',
    nonOrthogonality: 'Non-orthogonality',
    max: 'max',
    avg: 'avg',
    volume: 'Volume',
    stageRecovery: 'Recovery',
    stageGeometryPrep: 'Geometry prep',
    stageInputCheck: 'Input check',
    stageBoundaryCheck: 'Boundary check',
    stageMeshing: 'Meshing',
    stageQuality: 'Quality',
    stageHealing: 'Healing',
    stageConversion: 'Conversion',
    stageOpenfoamConversion: 'Format conversion',
    stagePolymeshAudit: 'Mesh audit',
    stageLaplacianSmooth: 'Smooth',
    stageQualityRetrigger: 'Quality Retrigger',
    stageQualityOptimization: 'Quality Opt',
    stageCavityExtract: 'Cavity Extract',
    stageChtConvert: 'CHT Convert',
    stageSplitRegions: 'Split Regions',
    stageCfmesh: 'Hex mesher',
    stagePolyMesh: 'Poly mesher',
    substageCaseBuild: 'Setup',
    substageBlockmesh: 'Background grid',
    substageFeatureExtract: 'Edge detection',
    substageDecompose: 'Split for parallel',
    substageSnap: 'Surface fitting',
    substageReconstruct: 'Merge results',
    substageSnapEdgeRecovery: 'Edge recovery',
    substageAddLayers: 'Boundary layers',
    substageCheckMesh: 'Quality check',
    badgeFluid: 'Fluid',
    badgeSolid: 'Solid',
    badgeBl: 'BL',
    logs: 'Logs',
    logsPreparing: 'Preparing…',
    logsDownloadFailed: 'Log download failed',
    partialMeshTitle: 'Partial mesh',
    partialMeshBody:
      'Meshing stopped after writing this mesh — preview it in the viewer to inspect what was produced. It may be incomplete.',
    partialMeshCells: 'Cells',
    partialMeshCellsTyped: '{{type}} cells',
    partialMeshFluidDomain: 'Fluid domain',
    partialMeshConnected: 'Connected',
    partialMeshDisconnected: 'Disconnected',
  },
  readinessReason: {
    feaPinchPoint: 'The part touches itself at a single point (pinch point)',
    feaNotWatertight: 'The surface has gaps / open edges (not a closed solid)',
    selfIntersection: 'Surfaces cross through each other',
    feaTopologyDefect: 'A topology defect',
    externalNonManifold: 'Bodies share internal walls / faces',
    externalOverlap: 'Two or more bodies overlap',
    externalPinchPoint: 'The part pinches to a point',
    externalSurfaceDefect: 'A surface defect',
    internalNoCavity: 'No sealed internal cavity could be extracted',
    // ENG-1677 made the cut a MeshGen step, so the copy names the step the user
    // hasn't taken rather than a defect. ENG-1884 dropped the "openings were
    // detected" clause with it: diagnosis no longer detects, so a skipped row
    // has looked for nothing, and claiming a finding it never made is the kind
    // of false confidence this line exists to remove.
    internalCavityNotExtracted:
      'The fluid cavity hasn’t been extracted yet — run Extract in MeshGen → Mesh Settings',
    // Extraction skipped + not_ready: the openings gate is what actually ran.
    // The cavity gate PASSED — a cavity exists and the amber is pinch /
    // self-intersection / overlap / non-manifold edges near its walls. Must not
    // say the extraction failed, because it did not.
    internalCavityDegraded:
      'A fluid cavity was extracted, but nearby geometry defects may degrade the mesh around it',
    internalNoOpenings:
      'No flow openings were detected, so there is nothing to extract a cavity from — cap the inlets/outlets or check the geometry',
    chtNotConformal:
      'Not a conformal multi-region assembly (needs ≥2 bodies sharing a matched interface)',
    chtRegionNotClosed: 'A region is not a closed solid',
    chtDegenerateFaces: 'Degenerate (zero-area) faces',
    chtRegionDefect: 'A region defect',
  },
  wrapSuggest: {
    title: 'This part will likely fail to mesh',
    description:
      'Diagnosis found {{blockers}}. These break the domain cut external-flow meshing relies on, so a normal mesh will fail — usually only after several minutes.',
    body: '<b>Wrap</b> prep seals the part into one clean, watertight surface that meshes reliably. Because prep is set at import, you’ll re-import the file to switch — the Wrap option will be ready for you.',
    continueAnyway: 'Continue anyway',
    reimport: 'Re-import with Wrap',
    noSolid: 'no solid volume (surfaces only)',
    pinch_one: '{{count}} pinch point',
    pinch_other: '{{count}} pinch points',
    overlap_one: '{{count}} interpenetrating body',
    overlap_other: '{{count}} interpenetrating bodies',
    openSurfaces: 'open surfaces (gaps)',
    topologyDefects: 'topology defects',
  },
  autoRetry: {
    label: 'Let the AI fix and retry failures',
    hint: 'If a mesh fails, or comes out below the quality bar, the AI reads what went wrong, changes the settings, and runs it again.',
    maxAttempts: 'Retries per mesh',
  },
  retry: {
    historyTitle: 'Retry history',
    historyLoadFailed: "Couldn't load retry history.",
    historyRetry: 'Retry',
    attempt: 'Attempt {{n}}',
    originalRun: 'Original run',
    aiBadge: 'AI · automatic',
    humanBadge: 'AI · manual',
    platformBadge: 'Platform re-dispatch',
    convergedBadge: 'Converged',
    diagnosisHeading: 'Diagnosis',
    changesHeading: 'What changed',
    humanEdited: '(edited before apply)',
    whatWasTried: '{{runs}} runs so far · up to {{max}} automatic retries',
    lowConfidenceTitle: 'The AI suggests a retry (low confidence)',
    escalatedTitle: 'Needs your review',
    declinedTitle: 'The AI recommends against retrying',
    applyAndRetry: 'Apply & retry',
    acceptBelowPolicy: 'Accept below policy',
    flagForReview: 'Flag for review',
    dismiss: 'Dismiss',
    dismissed: 'Dismissed',
    notePlaceholder: 'Optional note…',
    chatAction: 'Ask advisor',
    chatTooltip: 'Ask the retry advisor about this mesh',
    // One id, not a pasted digest: the advisor reads the mesh — and through it
    // the geometry — with the backend MCP tools under the reader's own JWT, so
    // a summary here would only give it a second, staler copy to disagree
    // with. `geometry_id` is nullable on the row, and naming a null geometry
    // in the prompt would be worse than letting the agent follow the link.
    chatPrompt:
      'Mesh {{meshId}} did not produce a usable mesh. Read that mesh — its geometry, its parameters, its quality metrics and its logs — and tell me why it came out this way and what you would change.',
    // Deterministic, never generated. A geometry whose every failed attempt
    // gets a chat would otherwise spend one title-generation call apiece.
    chatSessionTitle: 'Mesh retry advice — {{meshId}}',
    retryWithAi: 'Retry with AI',
    retryWithAiTooltip:
      'The agent reviews the failure, adjusts the mesh settings, and relaunches as a new attempt.',
    retryQueuedToast: 'Retry sent — the agent queued a new mesh attempt.',
    retryFailedToast: 'Retry failed: {{message}}',
    appliedToast: 'Suggestion applied — new mesh attempt queued.',
    dismissedToast: 'Suggestion dismissed.',
    qualityDelta: 'Quality before → after',
    policyPass: 'Policy pass',
    policyFail: 'Below policy',
    sopVersion: 'Retry SOP revision {{v}}',
    copy: 'Copy',
    copied: 'Copied',
    copyTooltip:
      'Copy the full retry log as plain text — ready to paste into a chat or message.',
    logHeader: 'Retry log · mesh {{id}}',
    // Tree marks. The chip text is short on purpose — a MeshGen tree row is
    // ~300px and already carries a cell count, an element type, a relative
    // time and a status dot — so the sentence lives in the paired tooltip.
    markNeedsYou: 'Needs you',
    markNeedsYouHint:
      'The AI has a fix ready and wants you to look at it before it runs.',
    markRetrying: 'AI retrying',
    markRetryingHint: 'The AI is running attempt {{n}}.',
    markFixed: 'AI fixed',
    markFixedHint:
      'The first attempt was no good. The AI fixed it on attempt {{n}}.',
    markStopped: 'AI stopped',
    markStoppedHint_one: 'The AI tried once more and could not fix it.',
    markStoppedHint_other: 'The AI tried {{count}} times and could not fix it.',
    // Rolled up onto a geometry row, so one glance covers every mesh under it.
    rollupFixed_one: '{{count}} fixed',
    rollupFixed_other: '{{count}} fixed',
    rollupNeedsYou_one: '{{count}} needs you',
    rollupNeedsYou_other: '{{count}} need you',
    // Workspace header — the count that pulls someone back to a waiting fix.
    headerNeedsYou_one: '{{count}} mesh needs you',
    headerNeedsYou_other: '{{count}} meshes need you',
    // Attempt switcher over the viewer.
    attemptSwitcherLabel: 'Attempt',
    attemptOutcomeFailed: 'failed',
    attemptOutcomeBelowBar: 'below the bar',
    attemptOutcomeRunning: 'running',
    attemptOutcomeGood: 'good',
    attemptMadeByAi: 'The AI made this attempt',
    attemptMadeByYou: 'Your original attempt',
    attemptChanged: 'What the AI changed',
  },
  feaJoins: {
    // ENG-2855: how a pair of bodies is joined in an FEA model — the
    // Diagnosis pair list, SimOps' Contact and Tied interfaces sections and
    // its mesh chips. Section names are quoted from simopsDraft
    // (thermal.tieSection, structural.contactSection). The structural draft
    // editor reaches contact pairs only; structural ties
    // (`tied_interfaces`, ENG-2568) are set through the API. The heat-flow
    // variant leaves contact out: a thermal draft offers ties alone.
    couplingLabel: 'How body pairs are joined in FEA',
    coupling:
      'An FEA model joins a pair of bodies in one of three ways:\n\nBonded: the bodies share one face and its nodes. The joint carries tension, shear and compression and never opens. You get it by meshing with Shared nodes — bonded in MeshGen.\n\nTie: two faces meshed separately are glued, so they move together even where their nodes don’t line up. Like a bond, it carries tension and never opens. SimOps adds ties for heat flow under Tied interfaces; for structural runs you set them through the API.\n\nContact: the faces push on each other but can separate, and they slide unless you set friction. It carries no tension and makes the solve nonlinear. Add it in SimOps under Contact.\n\nA tie or contact needs faces the mesh didn’t merge: a gap in a shared-node mesh, or any pair meshed with Independent bodies.',
    bonded: 'Bonded',
    bondedCaption: 'One face, shared nodes.',
    tie: 'Tie',
    tieCaption: 'Two meshes, glued.',
    contact: 'Contact',
    contactCaption: 'Pushes; can open or slide.',
    couplingThermalLabel: 'How body pairs are joined for heat flow',
    couplingThermal:
      'A heat-conduction model joins a pair of bodies in one of two ways:\n\nBonded: the bodies share one face and its nodes, so heat crosses it freely. You get it by meshing with Shared nodes — bonded in MeshGen.\n\nTie: two faces meshed separately are glued so they share one temperature, even where their nodes don’t line up. Add ties here under Tied interfaces; the first face you pick is the dependent side.\n\nFaces that only touch, with no bond or tie, pass no heat. A tie needs faces the mesh didn’t merge: a gap in a shared-node mesh, or any pair meshed with Independent bodies.',
  },
} as const;
